
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import web2mobile from './assets/vitrine/web2mobile.jpg';


/* WV_BOOTSTRAP_TOOLTIP_HELPER */
function showBsTooltip(el, message) {
  if (!el || !message) return;
  try {
    const bs = typeof window !== "undefined" && window.bootstrap ? window.bootstrap : null;
    if (!bs || !bs.Tooltip) return;

    el.setAttribute("data-bs-toggle", "tooltip");
    el.setAttribute("data-bs-placement", "top");
    el.setAttribute("data-bs-title", String(message));

    const existing = bs.Tooltip.getInstance(el);
    if (existing) existing.dispose();

    const tip = new bs.Tooltip(el, {
      trigger: "manual",
      title: String(message),
      placement: "top",
      container: "body",
      boundary: "viewport",
    });

    tip.show();

    window.setTimeout(() => {
      try {
        tip.hide();
        tip.dispose();
      } catch (_) { }
    }, 2200);
  } catch (e) {
    try { console.warn("Bootstrap tooltip failed", e); } catch (_) { }
  }
}
/* /WV_BOOTSTRAP_TOOLTIP_HELPER */


/** ******************************************************************************************
 * Wizard.js — COMPLETE FILE (based on your attached structure) with requested features
 * -------------------------------------------------------------------------------------------
 * ADDED EXACTLY AS REQUESTED:
 *  1) Toggle "Utiliser l’IA ?" before the Prompt field:
 *     - When ON: show Prompt (mandatory) + "Générer" button.
 *     - When OFF: hide Prompt entirely; manual scenario editing stays as-is.
 *  2) "Demander l’aide" with multi-select autocomplete:
 *     - Search users from /users.
 *     - Select many co-scénaristes; show chosen usernames as removable badges.
 *     - Saved to roles as role = "co-scenariste".
 *  3) Tags field under Description:
 *     - Chips input to add multiple tags.
 *     - Expect backend & DB changes: project_tags table + endpoints (documented below).
 *
 * Everything else remains aligned with your original layout and components.
 ******************************************************************************************* */

/** ***********************************
 * Utils & constants
 ************************************ */
const CO_SCEN_PAGE_SIZE = 36; // co-writer picker pagination (Step 1)
const TEAM_PAGE_SIZE = 10; // Team & sharing pagination (Step 2)

const WZ_LANG_KEY = "weavevo_lang";

/**
 * Wizard i18n (same logic as LandingPage.js):
 * Default language: English ("en")
 * Optional: French ("fr")
 *
 * NOTE: Language is controlled by SettingsPage.js via localStorage(WZ_LANG_KEY).
 */
const WZ_I18N = {
  en: {
    wz_wizard_title: "WeaveBlueprint",
    wz_template: "Layout:",
    wz_template_details_aria: "Template details",
    wz_step1: "Blueprint",
    wz_step2: "Weave access",
    wz_access_card_title: "Access",
    wz_access_in_loom: "Access when the weave is in the loom",
    wz_share_with_my_weavers: "Share with my Weavers",
    wz_select_weavers: "Select Weavers",
    wz_selected_weavers: "Selected Weavers",
    wz_more_details: "More details",
    wz_less_details: "Less details",
    wz_step3: "Slots",

    // Step 1 — identity & project fields
    wz_identity_title: "The Weave Cover *",
    wz_identity_pick: "Choose a file or drag it here",
    wz_identity_hint: "Images .jpg / .png < 20 MB",

    wz_project_title_label: "Weave Title",
    wz_project_title_ph: "e.g. The Ticket That Never Sleeps",
    wz_project_desc_label: "Describe the weave",
    wz_project_desc_ph: "Describe the weave…",

    wz_deadline_label: "Deadline",
    wz_deadline_ph: "Select a date…",

    // Templates
    wz_template_label: "The layout",
    wz_template_hint: "Choose how your Weavers will collaborate",
    wz_template_details_summary: "Template definition",
    wz_template_details_concept: "Concept",
    wz_template_details_how: "How it works",
    wz_template_details_example: "Example",
    wz_template_details_result: "Result",
    wz_template_details_nature: "Concept:",
    wz_template_details_logic: "How it works:",
    wz_template_details_engagement: "Application:",
    wz_template_details_output: "Result:",


    // Template texts
    tpl_SCRIPTED_label: "Storyboard",
    tpl_SCRIPTED_aka: "Precision Model",
    tpl_SCRIPTED_blurb: "A strict visual blueprint for perfect shot matching.",
    tpl_SCRIPTED_nature: "Precision Model: \"Define a sequence; every frame is a piece of the puzzle.\"",
    tpl_SCRIPTED_logic: "Contributors follow a strict visual blueprint (script and sketches) to ensure every shot fits perfectly into a pre-defined narrative.",
    tpl_SCRIPTED_engagement: "Recreating a specific camera movement or angle in your city to match a sequence filmed on the other side of the world.",
    tpl_SCRIPTED_output: "A tightly synchronized sequence where shots match across locations.",

    tpl_OPEN_label: "Gallery",
    tpl_OPEN_aka: "Diversity Model",
    tpl_OPEN_blurb: "An open call for many perspectives on one subject.",
    tpl_OPEN_nature: "Diversity Model: \"One subject, endless eyes. A mosaic of perspectives.\"",
    tpl_OPEN_logic: "An open call for a variety of perspectives on a single subject. There are no rules—just the beauty of different eyes seeing the same thing.",
    tpl_OPEN_engagement: "Collecting hundreds of different views of a landmark or event to create a massive visual mosaic or a high-speed montage.",
    tpl_OPEN_output: "A rich mosaic built from diverse viewpoints.",

    tpl_REACTION_label: "Seed",
    tpl_REACTION_aka: "Inspiration Model",
    tpl_REACTION_blurb: "One spark, endless interpretations.",
    tpl_REACTION_nature: "Inspiration Model: \"Plant a spark and let the community’s interpretation grow.\"",
    tpl_REACTION_logic: "We provide a single abstract theme or a \"spark,\" and you interpret it freely. It is designed to capture the \"soul\" of a subject.",
    tpl_REACTION_engagement: "Filming what \"Silence\" or \"History\" looks like in your local environment, leading to unexpected and poetic artistic shots.",
    tpl_REACTION_output: "An expressive, poetic collection of interpretations.",

    tpl_LIVE_INSTANT_label: "Symphony",
    tpl_LIVE_INSTANT_aka: "Sensory Model",
    tpl_LIVE_INSTANT_blurb: "Each person captures a different layer.",
    tpl_LIVE_INSTANT_nature: "Sensory Model: \"Unite video, ambient sound, and macro-textures into one harmony.\"",
    tpl_LIVE_INSTANT_logic: "A collaborative breakdown where each person handles a specific sensory layer (video, sound, or textures).",
    tpl_LIVE_INSTANT_engagement: "One person films the wide atmosphere, another records high-quality ambient sounds, and a third focuses on macro-details of surfaces.",
    tpl_LIVE_INSTANT_output: "A layered piece where visuals, sound, and textures work together.",

    tpl_TRAJECTORY_label: "Relay",
    tpl_TRAJECTORY_aka: "Continuity Model",
    tpl_TRAJECTORY_blurb: "A movement starts here, finishes elsewhere.",
    tpl_TRAJECTORY_nature: "Continuity Model: \"Build a bridge through motion. A gesture starts here and ends elsewhere.\"",
    tpl_TRAJECTORY_logic: "Creating a bridge through action. A movement or gesture starts in one location and is seamlessly completed in another.",
    tpl_TRAJECTORY_engagement: "An athlete running out of the frame in New York and appearing in the next frame running into the streets of Tokyo.",
    tpl_TRAJECTORY_output: "A seamless action handoff that feels like one continuous take.",

    tpl_DUEL_label: "Versus",
    tpl_DUEL_aka: "Contrast Model",
    tpl_DUEL_blurb: "Split-screen comparisons that reveal meaning.",
    tpl_DUEL_nature: "The Contrast Model.",
    tpl_DUEL_logic: "A systematic comparison using a split-screen format to highlight the relationship between two different realities.",
    tpl_DUEL_engagement: "Displaying \"Sunrise\" in the desert and \"Sunrise\" in a snowy city side-by-side to explore global symmetries.",
    tpl_DUEL_output: "A striking side-by-side that exposes contrasts and symmetries.",

    // AI
    wz_ai_card_title: "Draft the Layout",
    wz_enabled: "Enabled",
    wz_disabled: "Disabled",
    wz_ai_prompt_label: "AI prompt *",
    wz_ai_prompt_ph: "Describe the mission for your Weavers: What should they film? Where? In what style? (e.g., 'Skis in 4 unusual locations, focus on local landmarks and smooth transitions between clips').",
    wz_ai_slots_count_label: "Slots to generate",
    wz_ai_slots_count_help: "Guides the AI to generate exactly this number of slots (not saved).",
    wz_zoom_image: "Zoom image",
    wz_generate_steps: "Draft the Layout",

    // More settings
    wz_more_params: "More settings",
    wz_vizu_public_title: "Public preview",
    wz_vizu_public_help: "Show this project on the 'Explore' page to attract Weavers worldwide.",
    wz_details_public_title: "Public details",
    wz_details_public_help: "If enabled, the studio is accessible in read-only mode.",

    wz_tags: "Tags",
    wz_tags_ph: "e.g., #Adventure, #Travel, #Food",
    wz_no_tags: "No tags yet.",

    // Co-writers
    wz_ask_help: "Add Co-Creators",
    wz_search_user_ph: "Search a user…",
    wz_none_cos: "No co-writer selected.",
    wz_view_profile: "View profile",
    wz_view_profile_of: "View {username}'s profile",

    // Scenario
    wz_scenario_steps: "Slots",
    wz_no_steps: "No slot yet.",
    wz_add_step: "Add a slot",
    wz_save_draft: "Save draft",
    wz_next_step: "Next step",

    // Step card
    wz_step: "Slot {n}",
    wz_move_up: "Move up",
    wz_move_down: "Move down",
    wz_delete: "Delete",
    wz_place: "Where",
    wz_place_ph: "e.g., Eiffel Tower, Paris — or tap 📍 to use your position",
    wz_step_desc: "Mission",
    wz_step_desc_ph: "Short narrative text…",
    wz_generate_desc: "Mission AI Generation",

    wz_duration: "Duration",
    wz_duration_ph: "e.g., 5–8 seconds",
    wz_technical_tip: "Technical tip",
    wz_technical_tip_ph: "e.g., Hold your phone horizontally",
    wz_slot_illustration: "Illustration",
    wz_generate_image: "Generate image",
    wz_regenerate_image: "Regenerate",
    wz_remove_image: "Remove",
    wz_replace: "AI image update",
    wz_view: "View",
    wz_use_position: "Use my position",
    wz_position_unavailable: "Location not available on this device / browser.",
    wz_open_map: "Open map",
    wz_image_need_context: "Add at least a mission or a place before generating an image.",
    wz_image_failed: "Image generation failed.",
    wz_cover_need_context: "Add a title and a description before generating the cover.",
    wz_cover_generating: "Generating cover…",
    wz_cover_ai_hint: "Generate a cover from your title and description.",
    wz_generate_cover: "Generate cover",

    // Step 2 — team
    wz_team_share: "Team & sharing",
    wz_team_share_sub: "Select contributors (no email), compact and readable.",
    wz_search: "Search",
    wz_search_ph: "Name, surname, @username…",
    wz_profile_interests: "Interests (profile)",
    wz_no_interest_available: "No interest available.",
    wz_all_interests: "All interests",
    wz_loading: "Loading…",
    wz_result_count: "{n} result{s}",
    wz_page_of: "Page {page} / {total}",
    wz_prev: "Previous",
    wz_next: "Next",
    wz_no_results: "No results.",

    wz_visibility: "Visibility",
    wz_public: "Public",
    wz_private: "Private",
    wz_visibility_public_help: "Any user can contribute.",
    wz_visibility_private_help: "Only selected members can contribute.",

    wz_team: "Team",
    wz_click_remove: "Click to remove.",


    wz_add: "Add",
    wz_remove: "Remove",
    wz_added: "Added",
    wz_message_ph: "Write a message…",
    wz_tip: "Tip",


    wz_forum: "Forum",
    wz_user: "User",
    wz_email: "Email",
    wz_role: "Role",
    wz_profile_interests_col: "Interests (profile)",
    wz_none_option: "— None —",
    wz_role_contributor: "Contributor",
    wz_role_co_scenariste: "Co‑writer",
    wz_no_member_selected: "No member selected.",

    wz_back_prev: "Back to previous step",
    wz_publish_project: "Publish project",

    // Generic errors / alerts
    wz_enter_prompt: "Please enter a prompt.",
    wz_generation_failed: "Generation failed.",
    wz_error_ai: "AI error.",
    wz_session_expired: "Session expired.",
    wz_required_fields:
      "Image + Title + Description (and Prompt if AI enabled) are required.",
  }
  ,
  fr: {
    wz_wizard_title: "WeaveBlueprint",
    wz_template: "The layout:",
    wz_template_details_aria: "Détails du template",
    wz_step1: "Blueprint",
    wz_step2: "Accès Weave",
    wz_access_card_title: "Accès",
    wz_access_in_loom: "Accès quand la weave est dans le Loom",
    wz_share_with_my_weavers: "Partager avec mes Weavers",
    wz_select_weavers: "Sélectionner des Weavers",
    wz_selected_weavers: "Weavers sélectionnés",
    wz_more_details: "Plus de détails",
    wz_less_details: "Moins de détails",
    wz_step3: "Slots",

    wz_identity_title: "The Weave Cover *",
    wz_identity_pick: "Choisissez un fichier ou glissez-le ici",
    wz_identity_hint: "Images .jpg / .png < 20 Mo",

    wz_project_title_label: "Weave Title",
    wz_project_title_ph: "Ex: The Ticket That Never Sleeps",
    wz_project_desc_label: "Describe the weave",
    wz_project_desc_ph: "Describe the weave…",

    wz_deadline_label: "Deadline",
    wz_deadline_ph: "Sélectionnez une date…",

    wz_template_label: "The layout",
    wz_template_hint: "Choose how your Weavers will collaborate",
    wz_template_details_summary: "Définition du template",
    wz_template_details_concept: "Concept",
    wz_template_details_how: "Comment ça marche",
    wz_template_details_example: "Exemple",
    wz_template_details_result: "Résultat",
    wz_template_details_nature: "Concept :",
    wz_template_details_logic: "Fonctionnement :",
    wz_template_details_engagement: "Application :",
    wz_template_details_output: "Résultat :",


    // Textes des templates
    tpl_SCRIPTED_label: "Storyboard",
    tpl_SCRIPTED_aka: "Modèle de précision",
    tpl_SCRIPTED_blurb: "Un blueprint visuel strict pour des plans parfaitement assortis.",
    tpl_SCRIPTED_nature: "Precision Model: \"Define a sequence; every frame is a piece of the puzzle.\"",
    tpl_SCRIPTED_logic: "Les contributeurs suivent un blueprint visuel strict (script et croquis) afin que chaque plan s’intègre parfaitement dans un récit prédéfini.",
    tpl_SCRIPTED_engagement: "Reproduire un mouvement de caméra ou un angle précis dans votre ville pour correspondre à une séquence filmée à l’autre bout du monde.",
    tpl_SCRIPTED_output: "Une séquence synchronisée, avec des plans qui se répondent d’un lieu à l’autre.",

    tpl_OPEN_label: "Galerie",
    tpl_OPEN_aka: "Modèle de diversité",
    tpl_OPEN_blurb: "Un appel ouvert à des points de vue multiples sur un même sujet.",
    tpl_OPEN_nature: "Diversity Model: \"One subject, endless eyes. A mosaic of perspectives.\"",
    tpl_OPEN_logic: "Un appel ouvert à une variété de regards sur un seul sujet. Aucune règle — juste la beauté de différentes perspectives.",
    tpl_OPEN_engagement: "Collecter des centaines de vues différentes d’un lieu ou d’un événement pour créer une mosaïque visuelle massive ou un montage très rapide.",
    tpl_OPEN_output: "Une mosaïque riche construite à partir de points de vue variés.",

    tpl_REACTION_label: "Graine",
    tpl_REACTION_aka: "Modèle d’inspiration",
    tpl_REACTION_blurb: "Une étincelle, mille interprétations.",
    tpl_REACTION_nature: "Inspiration Model: \"Plant a spark and let the community’s interpretation grow.\"",
    tpl_REACTION_logic: "Nous donnons un thème abstrait unique, une « étincelle », et vous l’interprétez librement. L’objectif est de capturer l’âme d’un sujet.",
    tpl_REACTION_engagement: "Filmer ce que « Silence » ou « Histoire » évoque dans votre environnement local, pour des plans inattendus et poétiques.",
    tpl_REACTION_output: "Une collection expressive et poétique d’interprétations.",

    tpl_LIVE_INSTANT_label: "Symphonie",
    tpl_LIVE_INSTANT_aka: "Modèle sensoriel",
    tpl_LIVE_INSTANT_blurb: "Chacun capture une couche différente.",
    tpl_LIVE_INSTANT_nature: "Sensory Model: \"Unite video, ambient sound, and macro-textures into one harmony.\"",
    tpl_LIVE_INSTANT_logic: "Une collaboration où chaque personne gère une couche sensorielle spécifique (vidéo, son ou textures).",
    tpl_LIVE_INSTANT_engagement: "Une personne filme l’ambiance en plan large, une autre enregistre des sons d’ambiance de haute qualité, et une troisième se concentre sur les macro‑détails des surfaces.",
    tpl_LIVE_INSTANT_output: "Une pièce « en couches » où images, sons et textures se complètent.",

    tpl_TRAJECTORY_label: "Relais",
    tpl_TRAJECTORY_aka: "Modèle de continuité",
    tpl_TRAJECTORY_blurb: "Un geste commence ici, se termine ailleurs.",
    tpl_TRAJECTORY_nature: "Continuity Model: \"Build a bridge through motion. A gesture starts here and ends elsewhere.\"",
    tpl_TRAJECTORY_logic: "Créer un pont par l’action : un mouvement ou un geste commence dans un lieu et se termine de manière fluide dans un autre.",
    tpl_TRAJECTORY_engagement: "Un athlète sort du cadre à New York et réapparaît dans le plan suivant en courant dans les rues de Tokyo.",
    tpl_TRAJECTORY_output: "Une continuité d’action qui donne l’impression d’un seul plan.",

    tpl_DUEL_label: "Versus",
    tpl_DUEL_aka: "Modèle de contraste",
    tpl_DUEL_blurb: "Comparaison en split‑screen pour révéler les liens.",
    tpl_DUEL_nature: "Le modèle de contraste.",
    tpl_DUEL_logic: "Une comparaison systématique en split‑screen pour mettre en lumière la relation entre deux réalités différentes.",
    tpl_DUEL_engagement: "Afficher « Lever de soleil » dans le désert et « Lever de soleil » dans une ville enneigée côte à côte pour explorer les symétries globales.",
    tpl_DUEL_output: "Un face‑à‑face percutant qui révèle contrastes et symétries.",

    wz_ai_card_title: "Ébaucher la mise en page",
    wz_enabled: "Enabled",
    wz_disabled: "Disabled",
    wz_ai_prompt_label: "AI prompt *",
    wz_ai_prompt_ph: "Describe the mission for your Weavers: What should they film? Where? In what style? (e.g., 'Skis in 4 unusual locations, focus on local landmarks and smooth transitions between clips').",
    wz_ai_slots_count_label: "Nombre de slots à générer",
    wz_ai_slots_count_help: "Aide l’IA à générer exactement ce nombre de slots (non sauvegardé).",
    wz_zoom_image: "Agrandir l’image",
    wz_generate_steps: "Ébaucher la mise en page",

    wz_more_params: "Plus de paramètres",
    wz_vizu_public_title: "Visualisation public",
    wz_vizu_public_help: "Afficher ce projet sur la page « Explore » pour attirer des Weavers du monde entier.",
    wz_details_public_title: "Détails public",
    wz_details_public_help: "Si activé, le studio est accessible en lecture seule.",

    wz_tags: "Tags",
    wz_tags_ph: "ex. #Ski, #Aventure, #Voyage",
    wz_no_tags: "Pas de tag pour le moment.",

    wz_ask_help: "Ajouter des co-créateurs",
    wz_search_user_ph: "Chercher un utilisateur…",
    wz_none_cos: "Aucun co-scénariste sélectionné.",
    wz_view_profile: "Voir le profil",
    wz_view_profile_of: "Voir le profil de {username}",

    wz_scenario_steps: "Slots",
    wz_no_steps: "Aucun slot pour l’instant.",
    wz_add_step: "Ajouter un slot",
    wz_save_draft: "Enregistrer le brouillon",
    wz_next_step: "Étape suivante",

    wz_step: "Slot {n}",
    wz_move_up: "Monter",
    wz_move_down: "Descendre",
    wz_delete: "Supprimer",
    wz_place: "Where",
    wz_place_ph: "ex. Tour Eiffel, Paris — ou appuyez sur 📍 pour votre position",
    wz_step_desc: "Mission",
    wz_step_desc_ph: "Texte narratif court…",
    wz_generate_desc: "Génération IA de la mission",

    wz_duration: "Durée",
    wz_duration_ph: "ex. 5–8 secondes",
    wz_technical_tip: "Astuce technique",
    wz_technical_tip_ph: "ex. Filmez en horizontal",
    wz_slot_illustration: "Illustration",
    wz_generate_image: "Générer une image",
    wz_regenerate_image: "Régénérer",
    wz_remove_image: "Supprimer",
    wz_replace: "Mise à jour image IA",
    wz_view: "Voir",
    wz_use_position: "Utiliser ma position",
    wz_position_unavailable: "Localisation indisponible sur cet appareil / navigateur.",
    wz_open_map: "Ouvrir la carte",
    wz_image_need_context: "Ajoutez au moins une mission ou un lieu avant de générer une image.",
    wz_image_failed: "La génération d’image a échoué.",
    wz_cover_need_context: "Ajoutez un titre et une description avant de générer la couverture.",
    wz_cover_generating: "Génération de la couverture…",
    wz_cover_ai_hint: "Générez une couverture à partir du titre et de la description.",
    wz_generate_cover: "Générer une couverture",

    wz_team_share: "Équipe & Partage",
    wz_team_share_sub: "Sélectionnez les contributeurs (sans email), style compact et lisible.",
    wz_search: "Rechercher",
    wz_search_ph: "Nom, prénom, @username…",
    wz_profile_interests: "Intérêts (profil)",
    wz_no_interest_available: "Aucun centre d’intérêt disponible.",
    wz_all_interests: "Tous les intérêts",
    wz_loading: "Chargement…",
    wz_result_count: "{n} résultat{s}",
    wz_page_of: "Page {page} / {total}",
    wz_prev: "Précédent",
    wz_next: "Suivant",
    wz_no_results: "Aucun résultat.",

    wz_visibility: "Visibilité",
    wz_public: "Public",
    wz_private: "Privé",
    wz_visibility_public_help: "Tout utilisateur pourra contribuer.",
    wz_visibility_private_help: "Seuls les membres sélectionnés pourront contribuer.",

    wz_team: "Équipe",
    wz_click_remove: "Cliquez pour retirer.",


    wz_add: "Ajouter",
    wz_remove: "Retirer",
    wz_added: "Ajouté",
    wz_message_ph: "Écrire un message…",
    wz_tip: "Astuce",


    wz_forum: "Forum",
    wz_user: "Utilisateur",
    wz_email: "Email",
    wz_role: "Rôle",
    wz_profile_interests_col: "Centres d’intérêt (profil)",
    wz_none_option: "— Aucun —",
    wz_role_contributor: "Contributeur",
    wz_role_co_scenariste: "Co‑scénariste",
    wz_no_member_selected: "Aucun membre sélectionné.",

    wz_back_prev: "Retour à l'étape précédente",
    wz_publish_project: "Publier le projet",

    wz_enter_prompt: "Veuillez saisir un prompt.",
    wz_generation_failed: "La génération a échoué.",
    wz_error_ai: "Erreur IA.",
    wz_session_expired: "Session expirée.",
    wz_required_fields:
      "Image + Titre + Description (et Prompt si IA activée) sont requis.",
  }
};



/** ***********************************
 * Templates (project structure)
 * - Pure UI concept in the header (no layout changes)
 ************************************ */
const TEMPLATE_OPTIONS = [
  { key: 'SCRIPTED', icon: 'bi-clipboard2-check' },
  { key: 'OPEN', icon: 'bi-images' },
  { key: 'REACTION', icon: 'bi-lightbulb' },
  { key: 'LIVE_INSTANT', icon: 'bi-soundwave' },
  { key: 'TRAJECTORY', icon: 'bi-arrow-repeat' },
  { key: 'DUEL', icon: 'bi-columns-gap' },
];

const DEFAULT_TEMPLATE_KEY = 'SCRIPTED';

// Template visuals (served from frontend/public/landing)
const TEMPLATE_VISUALS = {
  SCRIPTED: '/landing/tmplt_storyboard.png',
  OPEN: '/landing/tmplt_gallery.png',
  REACTION: '/landing/tmplt_Seed.png',
  LIVE_INSTANT: '/landing/tmplt_Symphony.png',
  TRAJECTORY: '/landing/tmplt_Relay.png',
  DUEL: '/landing/templVersus.png',
};


const sanitizeTemplateKey = (key) => {
  const k = (key || '').toString().trim().toUpperCase();
  return TEMPLATE_OPTIONS.some((t) => t.key === k) ? k : DEFAULT_TEMPLATE_KEY;
};

const templateVisualSrc = (key) => TEMPLATE_VISUALS[sanitizeTemplateKey(key)] || '';


const getTemplateByKey = (key) => {
  const k = sanitizeTemplateKey(key);
  return TEMPLATE_OPTIONS.find((t) => t.key === k) || TEMPLATE_OPTIONS[0];
};

const templateTextKey = (templateKey, field) => `tpl_${sanitizeTemplateKey(templateKey)}_${field}`;

const localizeTemplate = (tpl, t) => {
  const tx = typeof t === 'function' ? t : (k) => k;
  const key = sanitizeTemplateKey(tpl?.key);

  const pick = (field) => {
    const k = templateTextKey(key, field);
    const v = tx(k);
    // If translation missing, fallback to template's default field (or empty).
    if (!v || v === k) return (tpl && tpl[field]) ? tpl[field] : '';
    return v;
  };

  return {
    ...tpl,
    key,
    label: pick('label'),
    aka: pick('aka'),
    blurb: pick('blurb'),
    nature: pick('nature'),
    logic: pick('logic'),
    engagement: pick('engagement'),
    output: pick('output'),
  };
};

const templateTooltip = (tpl, t) => {
  const tx = typeof t === 'function' ? t : (k) => k;
  const lt = localizeTemplate(tpl, t);
  return [
    `${lt.label} (${lt.aka})`,
    `${tx('wz_template_details_nature')} ${lt.nature}`,
    `${tx('wz_template_details_logic')} ${lt.logic}`,
    `${tx('wz_template_details_engagement')} ${lt.engagement}`,
    `${tx('wz_template_details_output')} ${lt.output}`,
  ].join("\n");
};

const mkStepId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
const newStep = (partial = {}) => ({
  id: mkStepId(),
  location: '',
  description: '',
  duration: '',
  technical_tip: '',
  image_url: '',
  reference: '',
  ...partial,
});
const normalize = (s) => (s || '').toString().toLowerCase();
const unique = (arr) => Array.from(new Set(arr));


const parseLatLng = (v) => {
  const m = String(v || '').match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
  if (!m) return null;
  const lat = parseFloat(m[1]);
  const lng = parseFloat(m[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};


// ✅ Robust sanitizer for interests/tags (prevents empty pills from invisible chars)
const INVISIBLE_RE = /[\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/g;
const NBSP_RE = /[\u00A0\u202F\u2007]/g;
const cleanToken = (v) => String(v ?? '')
  .replace(NBSP_RE, ' ')
  .replace(INVISIBLE_RE, '')
  .trim();

const parseTokenList = (raw) => {
  if (raw == null) return [];

  // Array => sanitize directly
  if (Array.isArray(raw)) {
    return raw.map(cleanToken).filter((t) => t && t.length > 0);
  }

  // String => sanitize + try JSON + split
  const s = cleanToken(raw);
  if (!s) return [];

  // Try JSON array (e.g. "[\"Art\",\"Musique\"]")
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) {
      return parsed.map(cleanToken).filter((t) => t && t.length > 0);
    }
  } catch (e) { }

  // Fallback split
  return s
    .split(/[,;|]/g)
    .map(cleanToken)
    .filter((t) => t && t.length > 0);
};


/**
 * Masonry helper (CSS grid row-span technique)
 * - Keeps DOM order intact
 * - Removes blank vertical gaps between cards (14" screens too)
 */
function computeWvMasonrySpans(gridEl, itemEls) {
  try {
    if (!gridEl || !itemEls || !itemEls.length) return;
    if (typeof window === "undefined") return;

    const styles = window.getComputedStyle(gridEl);
    const autoRows = parseFloat(styles.getPropertyValue("grid-auto-rows")) || 0;

    // prefer row-gap, fallback to gap
    const rowGap =
      parseFloat(styles.getPropertyValue("row-gap")) ||
      parseFloat(styles.getPropertyValue("grid-row-gap")) ||
      parseFloat(styles.getPropertyValue("gap")) ||
      0;

    if (!autoRows) return;

    itemEls.forEach((el) => {
      if (!el) return;
      const measureTarget = el.querySelector("[data-wv-masonry-measure]") || el;
      const h = measureTarget.getBoundingClientRect().height || 0;
      const span = Math.max(1, Math.ceil((h + rowGap) / (autoRows + rowGap)));
      el.style.setProperty("--wv-row-span", String(span));
    });
  } catch (_) {
    // noop
  }
}

const ACCOUNT_PAGE_DEFAULT_INTERESTS = [
  'Art',
  'Adventure',
  'Bien-être',
  'Cuisine',
  'Culture',
  'Écologie',
  'Histoire',
  'Nature',
  'Sciences',
  'Sport',
  'Technologie',
  'Voyage',
  'Aventure', // backward compat (older data)
];
/** ***********************************
 * Wizard — web route intentionally disabled
 * The full legacy web wizard is preserved below as WizardLegacy so it can be
 * restored later without losing any business logic.
 ************************************ */
const WIZARD_GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";
const WIZARD_IOS_WAITLIST_URL = "/join-beta";

export default function Wizard() {
  const [lang, setLang] = useState(() => {
    try {
      const raw = localStorage.getItem(WZ_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
      return String(raw).toLowerCase().startsWith("fr") ? "fr" : "en";
    } catch (_) {
      return "en";
    }
  });

  useEffect(() => {
    const refreshLang = () => {
      try {
        const raw = localStorage.getItem(WZ_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
        setLang(String(raw).toLowerCase().startsWith("fr") ? "fr" : "en");
      } catch (_) { }
    };
    window.addEventListener("storage", refreshLang);
    window.addEventListener("focus", refreshLang);
    return () => {
      window.removeEventListener("storage", refreshLang);
      window.removeEventListener("focus", refreshLang);
    };
  }, []);

  const copy = lang === "fr"
    ? {
        eyebrow: "CRÉATION MOBILE",
        title: "Créez votre prochaine Weave depuis l’app mobile.",
        body: "Le Blueprint, les slots, la géolocalisation, la capture média et les suggestions IA ont été pensés pour une expérience plus fluide sur téléphone.",
        google: "Télécharger sur Google Play",
        apple: "App Store / iOS",
        soon: "Bientôt disponible",
      }
    : {
        eyebrow: "MOBILE CREATION",
        title: "Create your next Weave from the mobile app.",
        body: "Blueprints, slots, geolocation, media capture and AI suggestions are designed for a smoother experience on your phone.",
        google: "Get it on Google Play",
        apple: "App Store / iOS",
        soon: "Coming soon",
      };

  return (
    <div className="wv-mobile-wizard-gate">
      <div className="wv-mobile-wizard-orb wv-mobile-wizard-orb-a" aria-hidden="true" />
      <div className="wv-mobile-wizard-orb wv-mobile-wizard-orb-b" aria-hidden="true" />

      <section className="wv-mobile-wizard-card" aria-labelledby="wv-mobile-wizard-title">
        <div className="wv-mobile-wizard-visual">
          <img
            src={web2mobile}
            alt="Weavevo web and mobile experience"
            className="wv-mobile-wizard-visual-img"
          />
        </div>

        <div className="wv-mobile-wizard-copy">
          <span className="wv-mobile-wizard-eyebrow">{copy.eyebrow}</span>
          <h1 id="wv-mobile-wizard-title">{copy.title}</h1>
          <p className="wv-mobile-wizard-lead">{copy.body}</p>


          <div className="wv-mobile-wizard-actions">
            <a
              className="wv-mobile-store-button is-google"
              href={WIZARD_GOOGLE_PLAY_URL}
              target="_blank"
              rel="noreferrer"
              aria-label={copy.google}
            >
              <i className="bi bi-google-play" />
              <span>{copy.google}</span>
            </a>

            <a
              className="wv-mobile-store-button is-apple"
              href={WIZARD_IOS_WAITLIST_URL}
              aria-label={`${copy.apple} — ${copy.soon}`}
            >
              <i className="bi bi-apple" />
              <span>
                <strong>{copy.apple}</strong>
                <small>{copy.soon}</small>
              </span>
            </a>
          </div>

        </div>
      </section>

      <style>{`
        .wv-mobile-wizard-gate,
        .wv-mobile-wizard-gate * { box-sizing: border-box; }
        .wv-mobile-wizard-gate {
          position: relative;
          min-height: calc(100vh - 70px);
          width: 100%;
          overflow: hidden;
          display: grid;
          place-items: center;
          padding: clamp(28px, 5vw, 72px);
          background:
            radial-gradient(circle at 18% 18%, rgba(70, 154, 255, .14), transparent 32%),
            radial-gradient(circle at 82% 74%, rgba(139, 92, 246, .12), transparent 34%),
            linear-gradient(180deg, #fbfdff 0%, #f5f8ff 100%);
          color: #08111e;
        }
        .wv-mobile-wizard-orb {
          position: absolute;
          width: 340px;
          height: 340px;
          border-radius: 50%;
          filter: blur(60px);
          opacity: .32;
          pointer-events: none;
        }
        .wv-mobile-wizard-orb-a { background: #5ab7ff; top: -150px; left: -80px; }
        .wv-mobile-wizard-orb-b { background: #8b5cf6; right: -120px; bottom: -170px; }
        .wv-mobile-wizard-card {
          position: relative;
          z-index: 1;
          width: min(1040px, 100%);
          min-height: 560px;
          display: grid;
          grid-template-columns: minmax(260px, .78fr) minmax(0, 1.22fr);
          align-items: center;
          gap: clamp(34px, 6vw, 86px);
          padding: clamp(34px, 5vw, 70px);
          border: 1px solid rgba(10, 32, 70, .09);
          border-radius: 34px;
          background: rgba(255, 255, 255, .91);
          box-shadow: 0 35px 100px rgba(21, 53, 95, .12);
          backdrop-filter: blur(18px);
        }
        .wv-mobile-wizard-visual {
          width: min(360px, 82vw);
          justify-self: center;
          border-radius: 28px;
          overflow: hidden;
          background: #eef3f8;
          box-shadow: 0 28px 64px rgba(8, 19, 37, .18);
          transform: rotate(-1.5deg);
        }
        .wv-mobile-wizard-visual-img {
          display: block;
          width: 100%;
          height: auto;
          object-fit: cover;
        }
        .wv-mobile-wizard-copy { min-width: 0; }
        .wv-mobile-wizard-eyebrow {
          display: inline-flex;
          padding: 7px 11px;
          border-radius: 999px;
          background: #eef8ff;
          color: #087ea4;
          font-size: 12px;
          font-weight: 850;
          letter-spacing: .13em;
        }
        .wv-mobile-wizard-copy h1 {
          margin: 18px 0 18px;
          max-width: 720px;
          font-size: clamp(2.5rem, 5vw, 4.8rem);
          line-height: .98;
          letter-spacing: -.055em;
          color: #07111f;
        }
        .wv-mobile-wizard-lead {
          max-width: 700px;
          margin: 0;
          color: #415169;
          font-size: clamp(1rem, 1.45vw, 1.17rem);
          line-height: 1.72;
        }
        .wv-mobile-wizard-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 28px; }
        .wv-mobile-store-button {
          min-height: 58px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 11px;
          padding: 10px 18px;
          border-radius: 16px;
          text-decoration: none !important;
          font-weight: 800;
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .wv-mobile-store-button:hover { transform: translateY(-2px); }
        .wv-mobile-store-button > i { font-size: 1.5rem; }
        .wv-mobile-store-button.is-google { background: #08111e; color: #fff; box-shadow: 0 14px 28px rgba(8,17,30,.18); }
        .wv-mobile-store-button.is-apple { border: 1px solid #d8e1ec; background: #fff; color: #08111e; }
        .wv-mobile-store-button.is-apple span { display: grid; line-height: 1.15; }
        .wv-mobile-store-button.is-apple small { margin-top: 3px; color: #6f7d91; font-size: 10px; font-weight: 700; }
        @media (max-width: 820px) {
          .wv-mobile-wizard-gate { padding: 22px 16px; }
          .wv-mobile-wizard-card { grid-template-columns: 1fr; padding: 30px 22px; border-radius: 26px; text-align: center; }
          .wv-mobile-wizard-visual { width: min(330px, 88vw); transform: none; }
          .wv-mobile-wizard-copy h1, .wv-mobile-wizard-lead { margin-left: auto; margin-right: auto; }
          .wv-mobile-wizard-actions { justify-content: center; }
        }
      `}</style>
    </div>
  );
}

/** ***********************************
 * Wizard (main — legacy web implementation preserved)
 ************************************ */
function WizardLegacy() {
  const { token: kc_token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();



  // Language (from SettingsPage): default EN, optional FR
  const [lang, setLang] = useState(() => {
    try {
      const raw =
        localStorage.getItem(WZ_LANG_KEY) ||
        localStorage.getItem("weavevo_language") ||
        "";
      const norm = String(raw || "").toLowerCase().slice(0, 2);
      return norm === "fr" ? "fr" : "en";
    } catch (e) {
      return "en";
    }
  });

  useEffect(() => {
    const read = () => {
      try {
        const raw =
          localStorage.getItem(WZ_LANG_KEY) ||
          localStorage.getItem("weavevo_language") ||
          "";
        const norm = String(raw || "").toLowerCase().slice(0, 2);
        const next = norm === "fr" ? "fr" : "en";
        setLang(next);
        if (!raw) localStorage.setItem(WZ_LANG_KEY, next);
        document.documentElement.lang = next;
      } catch (e) { }
    };

    read();
    window.addEventListener("storage", read);
    window.addEventListener("focus", read);
    document.addEventListener("visibilitychange", read);

    return () => {
      window.removeEventListener("storage", read);
      window.removeEventListener("focus", read);
      document.removeEventListener("visibilitychange", read);
    };
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = WZ_I18N?.[lang] || WZ_I18N?.en || {};
      let out = dict[key] ?? WZ_I18N?.en?.[key] ?? key;
      if (vars && typeof out === "string") {
        out = out.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));
      }
      return out;
    },
    [lang]
  );


  const tr = useCallback(
    (key, fallback, vars) => {
      const out = t(key, vars);
      return out === key ? (fallback ?? key) : out;
    },
    [t]
  );

  // -------- Router params
  const qs = new URLSearchParams(location.search);
  const edit = qs.get('edit') === 'true';
  const id = qs.get('id');

  // -------- Wizard navigation
  const [step, setStep] = useState(1); // 1 = weave definition, 2 = access & team, 3 = slots
  // Section anchors (single-page flow)
  const blueprintRef = useRef(null);
  const teamRef = useRef(null);
  const slotsRef = useRef(null);

  const goToStep = useCallback((next) => {
    setStep(next);
    const el = next === 1
      ? blueprintRef.current
      : next === 2
        ? teamRef.current
        : slotsRef.current;

    // Smooth scroll with a fixed header offset.
    if (el) {
      requestAnimationFrame(() => {
        try {
          const rect = el.getBoundingClientRect();
          const y = rect.top + window.pageYOffset - 96;
          window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
        } catch (e) {
          // Fallback
          if (typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    }
  }, []);


  // -------- Project fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('PRIVATE');

  // NEW: Template (header only)
  const [templateKey, setTemplateKey] = useState(DEFAULT_TEMPLATE_KEY);

  // NEW: Public flags
  const [vizuPublic, setVizuPublic] = useState(true); // visible in /explore
  const [detailsPublic, setDetailsPublic] = useState(true); // read-only studio access


  // NEW: Tags
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  // NEW: Use AI? toggle + Prompt
  const [useAI, setUseAI] = useState(true);
  const [prompt, setPrompt] = useState('');
  // NEW: AI slot count target (used only for AI generation, not saved)
  const [desiredSlotCount, setDesiredSlotCount] = useState('');

  // NEW: Zoom generated images
  const [zoomImageUrl, setZoomImageUrl] = useState(null);

  // Close zoom on ESC
  useEffect(() => {
    if (!zoomImageUrl) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setZoomImageUrl(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [zoomImageUrl]);


  // -------- Identity image
  const [projectIdentityFile, setProjectIdentityFile] = useState(null);
  const [projectIdentityPreview, setProjectIdentityPreview] = useState('');
  const [coverGenerating, setCoverGenerating] = useState(false);

  // -------- Scenario data
  const [scenarioTitle, setScenarioTitle] = useState('');
  const [scenarioDescription, setScenarioDescription] = useState('');
  const [scenarioSteps, setScenarioSteps] = useState([]);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [generatingStepIndex, setGeneratingStepIndex] = useState(-1);
  const [generatingImageIds, setGeneratingImageIds] = useState({});
  const isGeneratingImage = (stepId) => !!(generatingImageIds || {})[String(stepId)];
  const setGeneratingImage = (stepId, val) => {
    const k = String(stepId);
    setGeneratingImageIds(prev => {
      const next = { ...(prev || {}) };
      if (val) next[k] = true; else delete next[k];
      return next;
    });
  };

  // -------- Users / roles
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // NEW: multi-select co-scénaristes (badges)
  const [userQuery, setUserQuery] = useState('');
  const [selectedCoScens, setSelectedCoScens] = useState([]); // array of usernames
  const [selectedWeavers, setSelectedWeavers] = useState([]); // access: selected users
  const [weaversQuery, setWeaversQuery] = useState('');
  const [userPage, setUserPage] = useState(1);

  // Team table (existing)
  const [selectedContributors, setSelectedContributors] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [interestFilter, setInterestFilter] = useState('');
  const [interestMap, setInterestMap] = useState({});
  const [hasAnyProfileInterests, setHasAnyProfileInterests] = useState(false);
  const [page, setPage] = useState(1);

  // -------- Forum/messages
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  // -------- Tags parsing (so typed tags count even if user didn't click "+")
  const parseTagsFromText = useCallback((raw) => {
    const txt = String(raw || '').trim();
    if (!txt) return [];
    // allow "#Ski, #Travel" or "#Ski #Travel" or multiline
    const bySep = txt.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);
    const bySpace = bySep.flatMap(part => part.split(/\s+/).map(s => s.trim()).filter(Boolean));
    return bySpace;
  }, []);

  const mergedTags = useMemo(
    () => unique([...(Array.isArray(tags) ? tags : []), ...parseTagsFromText(tagInput)]),
    [tags, tagInput, parseTagsFromText]
  );

  // -------- Validation helpers
  const isDraftValid = useCallback(() => {
    // Draft: allow saving once basics are present.
    return !!title.trim() && !!description.trim();
  }, [title, description]);

  const isPublishValid = useCallback(() => {
    // Publish: cover + title + description + at least one tag.
    const hasPhoto = !!projectIdentityFile || !!projectIdentityPreview;
    const okBasics = hasPhoto && !!title.trim() && !!description.trim();
    return okBasics;
  }, [projectIdentityFile, projectIdentityPreview, title, description, mergedTags]);

  /** *******************************
     * Load: project + scenario + roles + messages
     ******************************** */
  useEffect(() => {
    if (!(edit && id)) return;

    // Project
    (async () => {
      try {
        const r = await fetch(`/projects/${id}`, { headers: { Authorization: `Bearer ${kc_token}` } });
        if (!r.ok) return;
        const p = await r.json();
        setTitle(p.title || '');
        setDescription(p.description || '');
        setScenarioDescription(prev => prev || p.description || '');
        setVisibility(p.visibility || 'PRIVATE');
        setVizuPublic(Boolean(p.vizu_public));
        setDetailsPublic(Boolean(p.details_public));
        setPrompt(p.prompt || '');
        setUseAI(Boolean(p.prompt && p.prompt.trim()));
        setTemplateKey(sanitizeTemplateKey(p.template_key || p.template || p.templateKey));
        if (Array.isArray(p.tags)) {
          setTags(p.tags);
        } else if (typeof p.tags === 'string' && p.tags.trim()) {
          try {
            const parsed = JSON.parse(p.tags);
            setTags(Array.isArray(parsed) ? parsed : []);
          } catch (e) {
            setTags(p.tags.split(',').map(t => t.trim()).filter(Boolean));
          }
        }

        if (p.identity_image) setProjectIdentityPreview(`/uploads/${p.identity_image}`);
      } catch (e) { /* noop */ }
    })();

    // Scenario
    (async () => {
      const normalizeSteps = (raw) => {
        let steps = [];
        try {
          const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
          steps = Array.isArray(arr) ? arr : [];
        } catch (e) { steps = []; }
        return steps.map(st => newStep({
          location: st.location || '',
          description: st.description || '',
          duration: st.duration || st.slot_duration || st.duration_sec || '',
          technical_tip: st.technical_tip || st.tip || st.technicalTip || '',
          image_url: st.image_url || st.imageUrl || st.illustration || '',
          reference: st.reference || '',
        }));
      };

      try {
        const r = await fetch(`/projects/${id}/scenarios`, { headers: { Authorization: `Bearer ${kc_token}` } });
        if (r.ok) {
          const s = await r.json();
          if (Array.isArray(s) && s.length) {
            const sc = s[0];
            setScenarioTitle(sc.title || '');
            setScenarioDescription(sc.description || '');
            setScenarioSteps(normalizeSteps(sc.steps));
            return;
          } else if (s && s.id) {
            setScenarioTitle(s.title || '');
            setScenarioDescription(s.description || '');
            setScenarioSteps(normalizeSteps(s.steps));
            return;
          }
        }
      } catch (e) { }
      try {
        const r2 = await fetch(`/projects/${id}/scenario`, { headers: { Authorization: `Bearer ${kc_token}` } });
        if (r2.ok) {
          const sc = await r2.json();
          if (sc) {
            setScenarioTitle(sc.title || '');
            setScenarioDescription(sc.description || '');
            setScenarioSteps(normalizeSteps(sc.steps));
          }
        }
      } catch (e) { }
    })();

    // Roles (we'll also hydrate co-scenaristes badges if present)
    (async () => {
      try {
        const r = await fetch(`/projects/${id}/roles`, { headers: { Authorization: `Bearer ${kc_token}` } });
        if (!r.ok) return;
        const rs = await r.json();
        if (Array.isArray(rs)) {
          setSelectedContributors(rs);
          const co = rs.filter(x => normalize(x.role) === 'co-scenariste').map(x => x.username);
          setSelectedCoScens(unique(co));

          // ✅ Persisted "selectedWeavers" (saved via roles — typically role="contributor")
          // Also accept older values if you already stored them differently.
          const weav = rs
            .filter(x => {
              const rr = normalize(x?.role);
              return rr === 'weaver' || rr === 'weavers' || rr === 'contributor' || rr === 'member' || rr === 'co-scenariste';
            })
            .map(x => x?.username)
            .filter(Boolean);

          setSelectedWeavers(unique(weav.map(cleanToken)));
        }
      } catch (e) { }
    })();

    // Messages
    (async () => {
      try {
        const r = await fetch(`/projects/${id}/messages`, { headers: { Authorization: `Bearer ${kc_token}` } });
        if (!r.ok) return;
        const arr = await r.json();
        setMessages(Array.isArray(arr) ? arr : []);
      } catch (e) { }
    })();
  }, [edit, id, kc_token]);

  /** *******************************
   * Load users + interests (for Step 1 help + Step 2 table)
   ******************************** */
  useEffect(() => {
    (async () => {
      try {
        setLoadingUsers(true);
        const r = await fetch('/users', { headers: { Authorization: `Bearer ${kc_token}` } });
        const arr = (r.ok ? await r.json() : []) || [];
        setUsers(Array.isArray(arr) ? arr : []);
      } catch (e) {
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, [kc_token]);


  useEffect(() => {
    const toInterestArray = (raw) => parseTokenList(raw);

    (async () => {
      try {
        const r = await fetch("/user_settings", {
          headers: { Authorization: `Bearer ${kc_token}` },
        });

        if (!r.ok) throw new Error("user_settings fetch failed");

        // Robust JSON handling (some proxies/nginx can mislabel content-type)
        let payload = null;
        const ct = (r.headers.get("content-type") || "").toLowerCase();

        if (ct.includes("application/json")) {
          payload = await r.json();
        } else {
          const txt = await r.text();
          try {
            payload = JSON.parse(txt);
          } catch (e) {
            payload = null;
          }
        }

        // Accept multiple shapes:
        //  - Array: [{ username, interests }, ...]
        //  - Object: { rows|data|items|users: [...] }
        const rows =
          Array.isArray(payload) ? payload :
            Array.isArray(payload?.rows) ? payload.rows :
              Array.isArray(payload?.data) ? payload.data :
                Array.isArray(payload?.items) ? payload.items :
                  Array.isArray(payload?.users) ? payload.users :
                    Array.isArray(payload?.result) ? payload.result :
                      [];

        const map = {};
        let any = false;

        (rows || []).forEach((row) => {
          const u = row?.username || row?.user || row?.login;
          const e = row?.email || row?.mail;
          const raw =
            row?.interests ??
            row?.tags ??
            row?.profileInterests ??
            row?.profile_interests ??
            row?.settings?.interests ??
            row?.settings?.tags;

          const arr = toInterestArray(raw);

          const ku = u ? normalize(cleanToken(u)) : "";
          const ke = e ? normalize(cleanToken(e)) : "";

          if (ku) map[ku] = arr;
          if (ke) map[ke] = arr;
          if (arr.length) any = true;
        });

        setInterestMap(any ? map : {});
        setHasAnyProfileInterests(any);
      } catch (e) {
        setInterestMap({});
        setHasAnyProfileInterests(false);
      }
    })();
  }, [kc_token]);


  /** *******************************
   * Identity helpers
   ******************************** */
  const fileInputRef = useRef(null);
  const coverAiBtnRef = useRef(null);
  const onDropIdentity = useCallback((e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (!f || !f.type?.startsWith('image/')) return;
    setProjectIdentityFile(f);
    setProjectIdentityPreview(URL.createObjectURL(f));
  }, []);

  const onPickIdentity = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f || !f.type?.startsWith('image/')) return;
    setProjectIdentityFile(f);
    setProjectIdentityPreview(URL.createObjectURL(f));
  }, []);

  /** *******************************
     * Cover AI generation (Title + Description → image)
     ******************************** */
  const handleGenerateCoverAI = async () => {
    const tt = (title || '').trim();
    const dd = (description || '').trim();
    if (!tt || !dd) {
      showBsTooltip(coverAiBtnRef.current, t('wz_cover_need_context'));
      return;
    }
    if (coverGenerating) return;

    setCoverGenerating(true);
    try {
      const compositePrompt = [
        'Create a cover image for a collaborative video project (Weave).',
        `Title: ${tt}`,
        `Description: ${dd}`,
        prompt ? `Global AI prompt: ${prompt}` : '',
        'Style: cinematic, inspiring, premium, high-quality illustration, smooth lighting.',
        'Constraints: no text, no logos, no watermarks.'
      ].filter(Boolean).join('\n');

      const res = await fetch('/ai/generate-slot-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${kc_token}` },
        body: JSON.stringify({
          prompt: compositePrompt,
          aspect_ratio: '16:9',
          image_size: '1K'
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'cover image failed');

      const url =
        data.image_url ||
        data.url ||
        data.imageUrl ||
        (data.b64 ? `data:image/png;base64,${data.b64}` : '') ||
        (data.base64 ? `data:image/png;base64,${data.base64}` : '');

      if (!url) throw new Error('no image url');

      // Turn the generated URL into a File so it will be uploaded with the project
      let blob = null;

      if (String(url).startsWith('data:')) {
        const parts = String(url).split(',');
        const meta = parts[0] || '';
        const b64 = parts[1] || '';
        const mime = (meta.match(/data:(.*?);base64/) || [])[1] || 'image/png';
        const bin = atob(b64);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        blob = new Blob([arr], { type: mime });
      } else {
        // Try to fetch the image binary (same-origin or public URL)
        const isRelative = String(url).startsWith('/');
        const r = await fetch(url, isRelative ? { headers: { Authorization: `Bearer ${kc_token}` } } : undefined);
        blob = await r.blob();
      }

      const mime = blob?.type || 'image/png';
      const ext = mime.includes('jpeg') ? 'jpg' : 'png';
      const file = new File([blob], `weave_cover_${Date.now()}.${ext}`, { type: mime });

      setProjectIdentityFile(file);
      setProjectIdentityPreview(URL.createObjectURL(file));
    } catch (e) {
      console.error(e);
      alert(t('wz_image_failed'));
    } finally {
      setCoverGenerating(false);
    }
  };

  /** *******************************
   * Scenario ops
   ******************************** */
  const addStep = () => setScenarioSteps(prev => [...prev, newStep()]);
  const removeStep = (sid) => setScenarioSteps(prev => prev.filter(s => s.id !== sid));
  const moveUp = (sid) => setScenarioSteps(prev => {
    const idx = prev.findIndex(s => s.id === sid);
    if (idx <= 0) return prev;
    const copy = [...prev];
    [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
    return copy;
  });
  const moveDown = (sid) => setScenarioSteps(prev => {
    const idx = prev.findIndex(s => s.id === sid);
    if (idx < 0 || idx === prev.length - 1) return prev;
    const copy = [...prev];
    [copy[idx + 1], copy[idx]] = [copy[idx], copy[idx + 1]];
    return copy;
  });
  const changeStep = (sid, draft) => setScenarioSteps(prev => prev.map(s => s.id === sid ? { ...s, ...draft } : s));

  /** *******************************
   * Tags (chips)
   ******************************** */
  const addTagFromInput = () => {
    const toAdd = parseTagsFromText(tagInput);
    if (!toAdd.length) return;
    setTags(prev => unique([...(Array.isArray(prev) ? prev : []), ...toAdd]));
    setTagInput('');
  };
  const removeTag = (tagText) => setTags(prev => prev.filter(x => x !== tagText));

  /** *******************************
   * Co-scénaristes (multi-select autocomplete)
   ******************************** */
  const filteredUsers = useMemo(() => {
    if (!userQuery.trim()) return []; // ✅ show nothing until typing starts
    return users.filter(u =>
      normalize(u.username).includes(normalize(userQuery)) ||
      normalize(u.firstName).includes(normalize(userQuery)) ||
      normalize(u.lastName).includes(normalize(userQuery)) ||
      normalize(u.email).includes(normalize(userQuery))
    );
  }, [users, userQuery]);


  const totalUserPages = Math.max(1, Math.ceil(filteredUsers.length / CO_SCEN_PAGE_SIZE));
  const safeUserPage = Math.min(userPage, totalUserPages);
  const visibleUserChoices = filteredUsers.slice((safeUserPage - 1) * CO_SCEN_PAGE_SIZE, safeUserPage * CO_SCEN_PAGE_SIZE);

  const toggleCoScen = (username) => {
    setSelectedCoScens(prev => prev.includes(username) ? prev.filter(x => x !== username) : [...prev, username]);
  };

  /** *******************************
   * AI: global generation & per-step
   ******************************** */

  // --- Concurrency helper (avoid flooding Imagen)
  const runWithConcurrency = async (items, limit, worker) => {
    const queue = (items || []).slice();
    const runners = new Array(Math.max(1, limit)).fill(0).map(async () => {
      while (queue.length) {
        const item = queue.shift();
        try { await worker(item); } catch (e) { /* ignore per-item */ }
      }
    });
    await Promise.all(runners);
  };

  const generateStepsFromPrompt = async () => {
    if (!prompt.trim()) { alert(t('wz_enter_prompt')); return; }
    try {
      setStepsLoading(true);
      const slotCount = parseInt(String(desiredSlotCount || '').trim(), 10);
      const enforcedPrompt = (Number.isFinite(slotCount) && slotCount > 0)
        ? `${prompt}\n\nSTRICT REQUIREMENT: You MUST generate exactly ${slotCount} slots (no more, no less). If you are unsure, still return exactly ${slotCount} slots by merging or simplifying ideas.`
        : prompt;

      const res = await fetch('/ai/generate-steps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${kc_token}` },
        body: JSON.stringify({ prompt: enforcedPrompt, title, description, max_slots: (Number.isFinite(slotCount) && slotCount > 0) ? slotCount : undefined })
      });
      const data = await res.json();
      if (data.steps && Array.isArray(data.steps)) {
        const mapped = data.steps.map(s => newStep({
          location: s.location || '',
          description: s.description || '',
          duration: s.duration || s.slot_duration || s.duration_sec || '',
          technical_tip: s.technical_tip || s.tip || s.technicalTip || '',
          image_url: s.image_url || s.imageUrl || s.illustration || '',
          reference: s.reference || ''
        }));
        setScenarioSteps(mapped);

        // Inline generation: text first, then images in the background (per slot)
        setTimeout(() => {
          const items = mapped
            .map((step, i) => ({ step, i }))
            .filter(x => !(x.step.image_url || '').trim());

          // Concurrency=2 to keep it fast but stable
          runWithConcurrency(items, 2, async ({ step, i }) => {
            await handleGenerateSlotImageAI(i, step);
          });
        }, 80);
      } else {
        alert(t('wz_generation_failed'));
      }
    } catch (e) {
      alert(t('wz_error_ai'));
    } finally {
      setStepsLoading(false);
    }
  };

  const handleGenerateStepAI = async (index) => {
    const s = scenarioSteps[index];
    if (!s || !s.location.trim()) return;
    setGeneratingStepIndex(index);
    try {
      const r = await fetch('/generate-doc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${kc_token}` },
        body: JSON.stringify({ title, location: s.location, promptContext: prompt || '' })
      });
      const data = await r.json();
      changeStep(s.id, { description: data?.documentary || '' });
    } catch (e) { }
    setGeneratingStepIndex(-1);
  };


  const handleGenerateSlotImageAI = async (index, stepOverride = null) => {
    const s = stepOverride || scenarioSteps[index];
    if (!s) return;

    const stepId = s.id || index;

    const where = (s.location || '').trim();
    const mission = (s.description || '').trim();
    const duration = (s.duration || '').trim();

    if (!where && !mission) {
      alert(t('wz_image_need_context'));
      return;
    }

    // Build a rich image prompt so the illustration matches the Weave + Slot context
    const compositePrompt = [
      title ? `Weave title: ${title}` : '',
      description ? `Weave description: ${description}` : '',
      prompt ? `Global AI prompt: ${prompt}` : '',
      where ? `Where: ${where}` : '',
      mission ? `Mission: ${mission}` : '',
      duration ? `Duration: ${duration}` : '',
      'Style: cinematic, inspiring, high-quality illustration, smooth lighting, realistic landscape/object focus.',
      'Constraints: no text, no logos, no watermarks.'
    ].filter(Boolean).join('\n');

    setGeneratingImage(stepId, true);
    try {
      const res = await fetch('/ai/generate-slot-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${kc_token}` },
        body: JSON.stringify({
          prompt: compositePrompt,
          aspect_ratio: '16:9',
          image_size: '1K'
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'image failed');

      const url =
        data.image_url ||
        data.url ||
        data.imageUrl ||
        (data.b64 ? `data:image/png;base64,${data.b64}` : '') ||
        (data.base64 ? `data:image/png;base64,${data.base64}` : '');

      if (!url) throw new Error('no image url');

      changeStep(s.id, { image_url: url });
    } catch (e) {
      console.error(e);
      alert(t('wz_image_failed'));
    } finally {
      setGeneratingImage(stepId, false);
    }
  };


  /** *******************************
   * Save: project + tags + roles + scenario
   ******************************** */
  const saveScenario = async (projectId) => {
    const stepsToSend = (scenarioSteps || []).map(({ id: _local, ...rest }) => ({
      location: rest.location || '',
      description: rest.description || '',
      duration: rest.duration || '',
      technical_tip: rest.technical_tip || '',
      image_url: rest.image_url || '',
      reference: rest.reference || '',
    }));
    const payload = {
      title: scenarioTitle || `Scénario: ${title}`,
      description: (scenarioDescription || description || ''),
      steps: JSON.stringify(stepsToSend),
    };
    const r = await fetch(`/projects/${projectId}/scenario`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${kc_token}` },
      body: JSON.stringify(payload),
    });
    return r.ok;
  };

  /** **************************************************************
 * Save project tags (PATCH /projects/:id)
 *************************************************************** */
  const saveTags = async (projectId) => {
    if (!projectId || !tags) return;
    try {
      await fetch(`/projects/${projectId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${kc_token}`,
        },
        body: JSON.stringify({ tags }),
      });
      console.log('✅ Tags saved:', tags);
    } catch (err) {
      console.error('❌ Error saving tags:', err);
    }
  };


  const saveRoles = async (projectId) => {
    // Persist access selection + co-scénaristes using the roles API.
    // IMPORTANT: avoid duplicate rows per user; keep a SINGLE role per username.
    // Priority: explicit roles table > co-scénariste > contributor (selected weavers).

    const roleByUser = new Map();

    // Existing roles table (if any)
    (selectedContributors || []).forEach(r => {
      const username = cleanToken(r?.username);
      const role = cleanToken(r?.role);
      if (username && role) roleByUser.set(username, role);
    });

    // Access: selected weavers => contributor by default
    (Array.isArray(selectedWeavers) ? selectedWeavers : []).forEach(u => {
      const username = cleanToken(u);
      if (!username) return;
      if (!roleByUser.has(username)) roleByUser.set(username, 'contributor');
    });

    // Co-scénaristes override contributor
    (selectedCoScens || []).forEach(u => {
      const username = cleanToken(u);
      if (!username) return;
      roleByUser.set(username, 'co-scenariste');
    });

    const roles = Array.from(roleByUser.entries())
      .map(([username, role]) => ({ username, role }))
      .filter(r => r.username && r.role);

    await fetch(`/projects/${projectId}/roles/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${kc_token}` },
      body: JSON.stringify({ roles })
    }).catch(() => { });
  };


  const handleSaveAll = async (forcedStatus) => {
    if (!kc_token) { alert(t('wz_session_expired')); return; }

    const isPublishing = (forcedStatus === 'Active');
    const tagsToSend = mergedTags;

    // ✅ Draft can be saved with basics; Publish requires all required fields.
    if (isPublishing) {
      if (!isPublishValid()) { alert(t('wz_required_fields')); return; }
    } else {
      if (!isDraftValid()) { alert(t('wz_required_fields')); return; }
    }

    // If user typed tags but didn't click "+", still count them
    if (tagInput && String(tagInput).trim()) {
      setTags(tagsToSend);
      setTagInput('');
    }

    try {
      const common = {
        title,
        description,
        status: forcedStatus || 'Draft',
        visibility,
        vizu_public: vizuPublic,
        details_public: detailsPublic,
        prompt: useAI ? prompt : '',
        tags: tagsToSend,
      };

      let projectId = (edit && id) ? id : null;

      if (!projectId) {
        let res;
        if (projectIdentityFile) {
          const fd = new FormData();
          Object.entries(common).forEach(([k, v]) => fd.append(k, v));
          fd.append('project_identity', projectIdentityFile);
          res = await fetch('/projects', { method: 'POST', headers: { Authorization: `Bearer ${kc_token}` }, body: fd });
        } else {
          res = await fetch('/projects', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${kc_token}` }, body: JSON.stringify(common) });
        }
        const created = await res.json().catch(() => ({}));
        projectId = created?.id;
        if (!res.ok || !projectId) throw new Error('create failed');
      } else {
        if (projectIdentityFile) {
          const fd = new FormData();
          Object.entries(common).forEach(([k, v]) => fd.append(k, v));
          fd.append('project_identity', projectIdentityFile);
          const up = await fetch(`/projects/${projectId}`, { method: 'PATCH', headers: { Authorization: `Bearer ${kc_token}` }, body: fd });
          if (!up.ok) throw new Error('update failed(file)');
        } else {
          const up = await fetch(`/projects/${projectId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${kc_token}` }, body: JSON.stringify(common) });
          if (!up.ok) throw new Error('update failed');
        }
      }

      await saveTags(projectId);
      await saveRoles(projectId);
      await saveScenario(projectId);
      if (forcedStatus === 'Active') {
        try {
          const res = await fetch(`/projects/${projectId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${kc_token}`,
            },
            body: JSON.stringify({ status: 'Active' }),
          });
          if (res.ok) {
            console.log('✅ Projet passé en statut Active.');
          } else {
            console.warn('⚠️ Impossible de mettre à jour le statut Active');
          }
        } catch (err) {
          console.error('❌ Erreur lors de la mise à jour du statut Active :', err);
        }
      }

      if (isPublishing) {
        navigate(`/project/${projectId}/studio`);
      } else {
        setTimeout(() => navigate('/portfolio'), 200);
      }

    } catch (e) {
      console.error("Erreur lors de l'enregistrement :", e);
    }
  };

  /** *******************************
   * Derived for Step 2 table
   ******************************** */
  const usersWithRole = useMemo(() => {
    return users.map(u => {
      const role = (selectedContributors.find(x => x.username === u.username)?.role) || '';

      // Interests can come from /user_settings (interestMap) OR directly from /users (fallback)
      const keyU = normalize(cleanToken(u.username));
      const keyE = normalize(cleanToken(u.email));
      const keyAttrE = normalize(cleanToken(u?.attributes?.email?.[0] || u?.attributes?.email));

      const intsFromMap =
        (interestMap && (interestMap[keyU] || interestMap[keyE] || interestMap[keyAttrE])) || [];

      const rawFallback = (u?.interests ?? u?.tags ?? u?.profile?.interests ?? u?.settings?.interests);
      const fallbackArr = parseTokenList(rawFallback);

      const finalArr = (Array.isArray(intsFromMap) && intsFromMap.length)
        ? intsFromMap
        : fallbackArr;

      return { ...u, role, interests: (finalArr || []).join(', ') };
    });
  }, [users, selectedContributors, interestMap]);

  const filteredUsersTable = useMemo(() => {
    return usersWithRole.filter(u => {
      const okSearch =
        !userSearch ||
        normalize(u.username).includes(normalize(userSearch)) ||
        normalize(u.firstName).includes(normalize(userSearch)) ||
        normalize(u.lastName).includes(normalize(userSearch)) ||
        normalize(u.email).includes(normalize(userSearch));
      const okInterest = !interestFilter || normalize(u.interests).includes(normalize(interestFilter));
      return okSearch && okInterest;
    });
  }, [usersWithRole, userSearch, interestFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsersTable.length / TEAM_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visibleUsers = filteredUsersTable.slice((safePage - 1) * TEAM_PAGE_SIZE, safePage * TEAM_PAGE_SIZE);

  /** *******************************
   * Forum/messages
   ******************************** */
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    const msg = { text: newMessage, createdAt: new Date().toISOString() };
    setMessages(prev => [...prev, msg]);
    setNewMessage('');
    try {
      if (edit && id) {
        await fetch(`/projects/${id}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${kc_token}` },
          body: JSON.stringify(msg)
        });
      }
    } catch (e) { }
  };

  /** *******************************
   * Stable handlers
   ******************************** */
  const onTitleChange = (e) => setTitle(e.target.value);
  const onDescriptionChange = (e) => setDescription(e.target.value);
  const onVisibilityChange = (vOrEvent) => {
    const v = (typeof vOrEvent === 'string')
      ? vOrEvent
      : (vOrEvent && vOrEvent.target ? vOrEvent.target.value : '');
    if (v) setVisibility(String(v).toUpperCase());
  };
  const onPromptChange = (e) => setPrompt(e.target.value);

  const onUserSearchChange = (e) => { setUserSearch(e.target.value); setPage(1); };
  const onInterestFilterChange = (e) => { setInterestFilter(e.target.value); setPage(1); };

  /** *******************************
   * Render
   ******************************** */
  return (
    <div className="container-fluid py-3 wizard-scope">
      <Header step={step} setStep={goToStep} templateKey={templateKey} setTemplateKey={setTemplateKey} t={t} />

      {/* Section 1: The Weave definition */}
      <div ref={blueprintRef} id="weave-definition" className="wv-section-anchor" />
      <Step1
        t={t}
        templateKey={templateKey}
        setTemplateKey={setTemplateKey}
        // identity
        fileInputRef={fileInputRef}
        projectIdentityPreview={projectIdentityPreview}
        onPickIdentity={onPickIdentity}
        onDropIdentity={onDropIdentity}
        onGenerateCoverAI={handleGenerateCoverAI}
        coverGenerating={coverGenerating}
        coverAiBtnRef={coverAiBtnRef}

        // fields
        title={title}
        description={description}
        onTitleChange={onTitleChange}
        onDescriptionChange={onDescriptionChange}

        // tags
        tags={tags}
        tagInput={tagInput}
        setTagInput={setTagInput}
        addTagFromInput={addTagFromInput}
        removeTag={removeTag}


        // access
        visibility={visibility}
        onVisibilityChange={onVisibilityChange}
        users={users}
        loadingUsers={loadingUsers}
        interestMap={interestMap}
        selectedWeavers={selectedWeavers}
        setSelectedWeavers={setSelectedWeavers}
        selectedCoScens={selectedCoScens}
        toggleCoScen={toggleCoScen}
        weaversQuery={weaversQuery}
        setWeaversQuery={setWeaversQuery}
      />

      {/* Section 2: Slots */}
      {/* Slots */}
      <div ref={slotsRef} id="weave-slots" className="wv-section-anchor" />
      <Step3
        t={t}

        // AI + slots
        useAI={useAI}
        setUseAI={setUseAI}
        prompt={prompt}
        onPromptChange={onPromptChange}
        desiredSlotCount={desiredSlotCount}
        setDesiredSlotCount={setDesiredSlotCount}
        generateStepsFromPrompt={generateStepsFromPrompt}

        // scenario
        scenarioSteps={scenarioSteps}
        stepsLoading={stepsLoading}
        generatingStepIndex={generatingStepIndex}
        generatingImageIds={generatingImageIds}
        addStep={addStep}
        removeStep={removeStep}
        moveUp={moveUp}
        moveDown={moveDown}
        changeStep={changeStep}
        handleGenerateStepAI={handleGenerateStepAI}
        handleGenerateSlotImageAI={handleGenerateSlotImageAI}
        onZoomImage={(url) => setZoomImageUrl(url)}

        // actions
        canSaveDraft={isDraftValid()}
        canPublish={isPublishValid()}
        onBack={() => goToStep(1)}
        onSaveDraft={() => handleSaveAll('Draft')}
        onPublish={() => handleSaveAll('Active')}
      />

      {/* Final actions (outside cards) */}
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-end gap-2 flex-wrap">
          <button
            className="btn btn-outline-primary btn-weave"
            onClick={() => handleSaveAll('Draft')}
            disabled={!isDraftValid()}
            type="button"
          >
            <i className="bi bi-cloud-check me-1"></i>
            {t('wz_save_draft')}
          </button>
          <button
            className="btn btn-primary btn-weave"
            onClick={() => handleSaveAll('Active')}
            disabled={!isPublishValid()}
            type="button"
          >
            <i className="bi bi-send-check me-1"></i>
            {t('wz_publish_project')}
          </button>
        </div>
      </div>

      {zoomImageUrl ? (
        <div
          className="wv-lightbox-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setZoomImageUrl(null)}
        >
          <div className="wv-lightbox" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="wv-lightbox-close"
              onClick={() => setZoomImageUrl(null)}
              aria-label="Close"
              title="Close"
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <img src={zoomImageUrl} alt={t('wz_zoom_image')} />
          </div>
        </div>
      ) : null}

      <WizardStyles />
    </div>
  );
}
/** ***********************************
 * Header (step navigation)
 ************************************ */
const Header = React.memo(function Header({ step, setStep, templateKey, setTemplateKey, t }) {
  const tpl = localizeTemplate(getTemplateByKey(templateKey), t);

  return (
    <div className="d-flex align-items-center justify-content-between mb-3">
      <div className="d-flex align-items-center gap-2">
        <i className="bi bi-camera-reels"></i>
        <div className="d-flex flex-column">
          <h3 className="m-0">{t('wz_wizard_title')}</h3>
          <div className="d-flex align-items-center gap-2 small text-muted">
            <span>{t('wz_template')}</span>
            <span className="badge text-bg-light border">{tpl.label}</span>
            <span className="d-none d-md-inline">{tpl.aka}</span>
            <i
              className="bi bi-info-circle"
              title={templateTooltip(tpl, t)}
              style={{ cursor: 'help' }}
              aria-label={t("wz_template_details_aria")}
            />
          </div>
        </div>
      </div>

      <div className="d-flex gap-2 flex-wrap align-items-center justify-content-end">
        <div className="d-flex gap-2">
          <button
            className={`btn btn-primary btn-weave ${step === 1 ? '' : 'wiz-step-inactive'}`}
            onClick={() => setStep(1)}
            type="button"
          >
            <i className="bi bi-1-circle"></i>
            {t('wz_step1')}
          </button>
          <button
            className={`btn btn-primary btn-weave ${step === 3 ? '' : 'wiz-step-inactive'}`}
            onClick={() => setStep(3)}
            type="button"
          >
            <i className="bi bi-2-circle"></i>
            {t('wz_step3')}
          </button>
        </div>
      </div>

    </div>
  );
});

/** ***********************************
 * Step 1 — project + scenario + (NEW) AI toggle + co-scenaristes + tags
 ************************************ */
/** ***********************************
 * Step 1 — The Weave definition (cover + title + description + tags + layout)
 ************************************ */
function Step1(props) {
  const {
    t,
    templateKey, setTemplateKey,

    // identity
    fileInputRef, projectIdentityPreview, onPickIdentity, onDropIdentity,
    onGenerateCoverAI, coverGenerating, coverAiBtnRef,

    // fields
    title, description, onTitleChange, onDescriptionChange,

    // tags
    tags, tagInput, setTagInput, addTagFromInput, removeTag,

    // access
    visibility,
    onVisibilityChange,
    users,
    loadingUsers,
    interestMap,
    selectedWeavers,
    setSelectedWeavers,
    selectedCoScens,
    toggleCoScen,
    weaversQuery,
    setWeaversQuery,
  } = props;

  // Masonry (CSS grid row-span technique) to remove blank gaps between the 4 cards
  // without changing content (keeps DOM order intact).
  const masonryGridRef = useRef(null);
  const masonryItemsRef = useRef([]);

  useEffect(() => {
    const grid = masonryGridRef.current;
    if (!grid || typeof window === "undefined") return;

    const getItems = () => (masonryItemsRef.current || []).filter(Boolean);

    const recalc = () => {
      computeWvMasonrySpans(grid, getItems());
    };

    let raf = 0;
    const schedule = () => {
      try {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(recalc);
      } catch (_) {
        recalc();
      }
    };

    // Initial + one more frame (images/fonts can change heights)
    schedule();
    const t1 = window.setTimeout(schedule, 120);

    let ro = null;
    try {
      if (typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(() => schedule());
        getItems().forEach((el) => ro.observe(el));
        ro.observe(grid);
      }
    } catch (_) {
      ro = null;
    }

    window.addEventListener("resize", schedule);

    return () => {
      window.clearTimeout(t1);
      window.removeEventListener("resize", schedule);
      try { if (raf) cancelAnimationFrame(raf); } catch (_) { }
      if (ro) ro.disconnect();
    };
  }, []);

  return (
    <div className="container-fluid">
      <div ref={masonryGridRef} className="wv-masonry-grid">
        {/* Cover */}
        <div ref={(el) => (masonryItemsRef.current[0] = el)} className="wv-masonry-item">
          <div data-wv-masonry-measure>
            <IdentityCard
              t={t}
              fileInputRef={fileInputRef}
              preview={projectIdentityPreview}
              onPick={onPickIdentity}
              onDrop={onDropIdentity}
              onGenerateAI={onGenerateCoverAI}
              generatingAI={coverGenerating}
              aiBtnRef={coverAiBtnRef}
            />
          </div>
        </div>

        {/* Info générale */}
        <div ref={(el) => (masonryItemsRef.current[1] = el)} className="wv-masonry-item">
          <div data-wv-masonry-measure>
            <ProjectFields
              t={t}
              title={title}
              description={description}
              onTitleChange={onTitleChange}
              onDescriptionChange={onDescriptionChange}
              tags={tags}
              tagInput={tagInput}
              setTagInput={setTagInput}
              addTagFromInput={addTagFromInput}
              removeTag={removeTag}
            />
          </div>
        </div>

        {/* Access */}
        <div ref={(el) => (masonryItemsRef.current[2] = el)} className="wv-masonry-item">
          <div data-wv-masonry-measure>
            <AccessCard
              t={t}
              visibility={visibility}
              onVisibilityChange={onVisibilityChange}
              users={users}
              loadingUsers={loadingUsers}
              interestMap={interestMap}
              selectedWeavers={selectedWeavers}
              setSelectedWeavers={setSelectedWeavers}
              selectedCoScens={selectedCoScens}
              toggleCoScen={toggleCoScen}
              weaversQuery={weaversQuery}
              setWeaversQuery={setWeaversQuery}
            />
          </div>
        </div>

        {/* Layout */}
        <div ref={(el) => (masonryItemsRef.current[3] = el)} className="wv-masonry-item">
          <div data-wv-masonry-measure>
            <TemplatePicker templateKey={templateKey} setTemplateKey={setTemplateKey} t={t} />
          </div>
        </div>
      </div>
    </div>
  );
}



function Step2(props) {
  const {
    t,

    // access flags
    vizuPublic, setVizuPublic,
    detailsPublic, setDetailsPublic,

    // visibility
    visibility, onVisibilityChange,

    // co-creators
    loadingUsers, users, userQuery, setUserQuery, visibleUserChoices, selectedCoScens, toggleCoScen, setUserPage,

    // Team table
    hasAnyProfileInterests,
    interestMap,
    userSearch,
    interestFilter,
    onUserSearchChange,
    onInterestFilterChange,
    page,
    setPage,
    totalPages,
    visibleUsers,
    filteredUsersTable,
    selectedContributors,
    onToggleContributor,
    onSetContributorRole,

    // forum
    messages,
    newMessage,
    onNewMessageChange,
    onSendMessage,

    // navigation
    onBack,
    onNext,
  } = props;

  const DEFAULT_INTERESTS = Array.isArray(ACCOUNT_PAGE_DEFAULT_INTERESTS)
    ? ACCOUNT_PAGE_DEFAULT_INTERESTS
    : [];

  const allInterests = useMemo(() => {
    const acc = [];
    // defaults (always show the full list)
    (DEFAULT_INTERESTS || []).forEach((x) => { if (x) acc.push(String(x)); });

    // from interestMap (/user_settings)
    try {
      Object.values(interestMap || {}).forEach((arr) => {
        (Array.isArray(arr) ? arr : parseTokenList(arr)).forEach((it) => { if (it) acc.push(it); });
      });
    } catch (e) { }

    // from users (fallback)
    try {
      (Array.isArray(users) ? users : []).forEach((u) => {
        const raw = (u?.interests ?? u?.tags ?? u?.profile?.interests ?? u?.settings?.interests);
        parseTokenList(raw).forEach((it) => { if (it) acc.push(it); });
      });
    } catch (e) { }

    return Array.from(new Set(acc.map((x) => cleanToken(String(x))))).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [users, interestMap]);

  const hasAnyInterests = allInterests.length > 0;
  const [accessOpen, setAccessOpen] = useState(false);

  return (
    <div className="container-fluid">
      {/* Weave access (collapsed by default) */}
      <div className="card border-0 rounded-4 shadow-sm mb-4">
        <div className="card-body p-3 p-md-4">
          <button
            type="button"
            className="btn w-100 p-0 text-start d-flex align-items-center justify-content-between gap-3 wv-plain-btn"
            onClick={() => setAccessOpen((v) => !v)}
            aria-expanded={accessOpen ? 'true' : 'false'}
            aria-controls="wv-weave-access-collapse"
          >
            <span className="d-flex align-items-center gap-2">
              <i className="bi bi-shield-lock"></i>
              <span className="fw-bold">{t('wz_step1')}</span>
            </span>
            <i className={`bi ${accessOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true"></i>
          </button>

          <div id="wv-weave-access-collapse" className={`collapse ${accessOpen ? 'show' : ''}`}>
            <div className="pt-3">
              <div className="row g-3">
                <div className="col-12 col-lg-6">
                  <div className="border rounded-4 p-3 bg-white">
                    <div className="d-flex align-items-start justify-content-between gap-3">
                      <div>
                        <div className="fw-semibold">{t('wz_vizu_public_title')}</div>
                        <div className="small text-muted">{t('wz_vizu_public_help')}</div>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!vizuPublic}
                          onChange={(e) => setVizuPublic(e.target.checked)}
                          id="wiz-vizu-public"
                        />
                      </div>
                    </div>

                    <hr className="my-3" />

                    <div className="d-flex align-items-start justify-content-between gap-3">
                      <div>
                        <div className="fw-semibold">{t('wz_details_public_title')}</div>
                        <div className="small text-muted">{t('wz_details_public_help')}</div>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!detailsPublic}
                          onChange={(e) => setDetailsPublic(e.target.checked)}
                          id="wiz-details-public"
                        />
                      </div>
                    </div>

                    <hr className="my-3" />

                    <label className="form-label fw-bold mb-1">{t('wz_visibility')}</label>
                    <select className="form-select rounded-4" value={visibility} onChange={onVisibilityChange}>
                      <option value="PRIVATE">{t('wz_private')}</option>
                      <option value="PUBLIC">{t('wz_public')}</option>
                    </select>
                  </div>
                </div>

                <div className="col-12 col-lg-6">
                  {/* Add Co-Creators */}
                  <div className="border rounded-4 p-3 bg-white">
                    <h6 className="mb-3 fw-bold">
                      <i className="bi bi-people me-2"></i>{t('wz_ask_help')}
                    </h6>

                    <input
                      className="form-control mb-2"
                      placeholder={t('wz_search_user_ph')}
                      value={userQuery}
                      onChange={(e) => {
                        setUserQuery(e.target.value);
                        if (typeof setUserPage === 'function') setUserPage(1);
                      }}
                    />

                    <div className="d-flex flex-wrap gap-3 mb-3">
                      {selectedCoScens?.length ? (
                        selectedCoScens.map((username) => {
                          const u = users.find((x) => x.username === username);
                          const avatarSrc = u?.avatar_url || "/default-avatar.png";
                          return (
                            <div key={username} className="text-center position-relative">
                              <a
                                href={`/profile/${username}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={t('wz_view_profile_of', { username })}
                              >
                                <img
                                  src={avatarSrc}
                                  alt={username}
                                  style={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    border: "2px solid #111827",
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.src = "/default-avatar.png";
                                  }}
                                />
                              </a>
                              <button
                                type="button"
                                className="btn btn-sm btn-link position-absolute top-0 end-0 p-0"
                                onClick={() => toggleCoScen(username)}
                                title={t('wz_delete')}
                              >
                                <i className="bi bi-x-circle-fill text-danger"></i>
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-muted small">{t('wz_none_cos')}</div>
                      )}
                    </div>

                    {(loadingUsers || (userQuery?.trim() && visibleUserChoices?.length > 0)) && (
                      <div className="border rounded-3 p-2" style={{ maxHeight: 220, overflow: "auto" }}>
                        {loadingUsers && (
                          <div className="text-muted">
                            <i className="bi bi-arrow-repeat me-2"></i>{t('wz_loading')}
                          </div>
                        )}

                        {!loadingUsers && userQuery?.trim() && visibleUserChoices?.length === 0 && (
                          <div className="text-muted">{t('wz_no_results')}</div>
                        )}

                        {!loadingUsers &&
                          userQuery?.trim() &&
                          (visibleUserChoices || []).map((u) => (
                            <div
                              key={u.username}
                              className="d-flex align-items-center justify-content-between border rounded p-2 mb-2 bg-light"
                              style={{ cursor: "pointer" }}
                              onClick={() => toggleCoScen(u.username)}
                            >
                              <div className="d-flex align-items-center gap-3">
                                <img
                                  src={u?.avatar_url || "/default-avatar.png"}
                                  alt={u.username}
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.src = "/default-avatar.png";
                                  }}
                                />
                                <div>
                                  <div className="fw-semibold">{u.username}</div>
                                </div>
                              </div>

                              <a
                                href={`/profile/${u.username}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary btn-sm"
                                title={t('wz_view_profile')}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <i className="bi bi-person-lines-fill"></i>
                              </a>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between mt-3">
            <button type="button" className="btn btn-outline-secondary btn-weave" onClick={onBack}>
              <i className="bi bi-arrow-up-circle me-1"></i>{t('wz_step1')}
            </button>
            <button type="button" className="btn btn-primary btn-weave" onClick={onNext}>
              <i className="bi bi-arrow-down-circle me-1"></i>{t('wz_step3')}
            </button>
          </div>
        </div>
      </div>

      {/* Team & sharing */}
      <div className="card border-0 rounded-4 shadow-sm">
        <div className="card-body p-3 p-md-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div>
              <div className="fw-bold">{t('wz_team_share')}</div>
              <div className="small text-muted">{t('wz_team_share_sub')}</div>
            </div>
          </div>

          <div className="row g-3">
            {/* LEFT: Users list */}
            <div className="col-12 col-xxl-8">
              <div className="d-flex gap-2 flex-wrap mb-2">
                <div className="flex-grow-1">
                  <label className="form-label fw-bold mb-1">{t('wz_search')}</label>
                  <input
                    className="form-control rounded-4"
                    value={userSearch}
                    onChange={onUserSearchChange}
                    placeholder={t('wz_search_ph')}
                  />
                </div>

                <div style={{ minWidth: 240 }}>
                  <label className="form-label fw-bold mb-1">{t('wz_profile_interests')}</label>
                  <select
                    className="form-select rounded-4"
                    value={interestFilter}
                    onChange={onInterestFilterChange}
                    disabled={!hasAnyInterests}
                  >
                    <option value="">{t('wz_all_interests')}</option>
                    {(allInterests || []).map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                  {!hasAnyInterests && (
                    <div className="form-text text-muted">{t('wz_no_interest_available')}</div>
                  )}
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 56 }}></th>
                      <th>{t('wz_user')}</th>
                      <th style={{ width: 380 }}>{t('wz_profile_interests')}</th>
                      <th style={{ width: 120 }} className="text-end">{t('wz_action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(visibleUsers || []).map((u) => {
                      const selected = selectedContributors?.some((c) => c.username === u.username);
                      const avatarSrc = u?.avatar_url || "/default-avatar.png";
                      return (
                        <tr key={u.username}>
                          <td>
                            <img
                              src={avatarSrc}
                              alt=""
                              style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
                              onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }}
                            />
                          </td>
                          <td>
                            <div className="fw-semibold">{u.username}</div>
                            <div className="small text-muted">
                              {(u.firstName || '')} {(u.lastName || '')}
                            </div>
                          </td>
                          <td>
                            {(() => {
                              const arr = parseTokenList(u?.interests).slice(0, 6);
                              if (!arr.length) return <span className="text-muted small">—</span>;
                              return (
                                <div className="d-flex flex-wrap gap-1">
                                  {arr.map((it) => (
                                    <span key={it} className="badge text-bg-light border">
                                      {it}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className={`btn btn-sm ${selected ? 'btn-outline-danger' : 'btn-outline-primary'} btn-weave`}
                              onClick={() => onToggleContributor(u)}
                            >
                              {selected ? t('wz_remove') : t('wz_add')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="d-flex align-items-center justify-content-between mt-2">
                <div className="small text-muted">
                  {t('wz_page')} {page} / {totalPages}
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary btn-weave"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary btn-weave"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* RIGHT: Selected Team */}
            <div className="col-12 col-xxl-4">
              <div className="border rounded-4 p-3 bg-white">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="fw-semibold">Team ({selectedContributors?.length || 0})</div>
                </div>

                <div className="small text-muted mb-3">
                  {selectedContributors?.length
                    ? "Selected members"
                    : "No team member selected yet."}
                </div>

                <div style={{ maxHeight: 340, overflow: "auto" }}>
                  {selectedContributors?.length ? (
                    selectedContributors.map((c) => {
                      const u = (Array.isArray(users) ? users : []).find((x) => x.username === c.username) || { username: c.username };
                      const avatarSrc = u?.avatar_url || "/default-avatar.png";

                      return (
                        <div key={c.username} className="border rounded-4 p-2 mb-2">
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src={avatarSrc}
                                alt=""
                                style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover" }}
                                onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }}
                              />
                              <div>
                                <div className="fw-semibold">{u.username}</div>
                              </div>
                            </div>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger btn-weave"
                              onClick={() => onToggleContributor(u)}
                              title={t('wz_remove')}
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-muted small">
                      {t('wz_tip')}: {t('wz_add')} / {t('wz_remove')}
                    </div>
                  )}
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>
    </div>
  );
}

/** ***********************************
 * Step 3 — Slots (AI-assisted or manual)
 ************************************ */
function Step3(props) {
  const {
    t,

    // AI + prompt
    useAI,
    setUseAI,
    prompt,
    onPromptChange,
    desiredSlotCount,
    setDesiredSlotCount,
    generateStepsFromPrompt,

    // slots
    scenarioSteps,
    stepsLoading,
    generatingStepIndex,
    generatingImageIds,
    addStep,
    removeStep,
    moveUp,
    moveDown,
    changeStep,
    handleGenerateStepAI,
    handleGenerateSlotImageAI,
    onZoomImage,

    // actions
    canSaveDraft,
    canPublish,
    onBack,
    onSaveDraft,
    onPublish,
  } = props;

  return (
    <div className="container-fluid">
      <div className="card border-0 rounded-4 shadow-sm">
        <div className="card-body p-3 p-md-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-list-ol"></i>
              <span className="fw-bold">{t('wz_generate_steps')}</span>
            </div>

          </div>

          {/* AI generator */}
          <div className="wv-ai-block rounded-4 mb-4">
            <div className="wv-ai-head">


              <div className="d-flex align-items-center gap-2">
                <span className="small text-muted">{useAI ? t('wz_enabled') : t('wz_disabled')}</span>
                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={!!useAI}
                    onChange={(e) => setUseAI(e.target.checked)}
                    id="wiz-use-ai"
                  />
                </div>
              </div>
            </div>

            {useAI ? (
              <>
                <div className="mt-3">
                  <label className="form-label fw-bold">{t('wz_ai_prompt_label')}</label>
                  <textarea
                    className="form-control rounded-4 wv-ai-textarea"
                    rows={4}
                    value={prompt}
                    onChange={onPromptChange}
                    placeholder={t('wz_ai_prompt_ph')}
                  />
                </div>

                <div className="wv-ai-bottom">
                  <div className="wv-ai-slot">
                    <label className="form-label fw-bold">{t('wz_ai_slots_count_label')}</label>

                    <div className="wv-stepper" role="group" aria-label={t('wz_ai_slots_count_label')}>
                      <button
                        type="button"
                        className="wv-stepper-btn"
                        onClick={() => {
                          const cur = parseInt(String(desiredSlotCount || '').trim(), 10);
                          const next = Math.max(1, (Number.isFinite(cur) ? cur : 1) - 1);
                          setDesiredSlotCount(String(next));
                        }}
                        aria-label="Decrease"
                      >
                        <i className="bi bi-dash-lg" aria-hidden="true"></i>
                      </button>

                      <input
                        className="wv-stepper-input"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        value={desiredSlotCount}
                        onChange={(e) => setDesiredSlotCount(e.target.value)}
                      />

                      <button
                        type="button"
                        className="wv-stepper-btn"
                        onClick={() => {
                          const cur = parseInt(String(desiredSlotCount || '').trim(), 10);
                          const next = Math.min(50, (Number.isFinite(cur) ? cur : 1) + 1);
                          setDesiredSlotCount(String(next));
                        }}
                        aria-label="Increase"
                      >
                        <i className="bi bi-plus-lg" aria-hidden="true"></i>
                      </button>
                    </div>
                    <div className="form-text text-muted">{t('wz_ai_slots_count_help')}</div>
                  </div>

                  <div className="wv-ai-action">
                    <button
                      type="button"
                      className="btn btn-primary btn-weave wv-ai-generate-btn"
                      onClick={generateStepsFromPrompt}
                      disabled={!prompt.trim() || stepsLoading}
                    >
                      {stepsLoading ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      ) : (
                        <i className="bi bi-stars" aria-hidden="true"></i>
                      )}
                      {t('wz_generate_steps')}
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>

          {/* Slots list */}
          {stepsLoading ? (
            <div className="text-muted">
              <i className="bi bi-arrow-repeat me-2"></i>{t('wz_loading')}
            </div>
          ) : (
            <>
              <StepsList
                steps={scenarioSteps}
                generatingIndex={generatingStepIndex}
                generatingImageIds={generatingImageIds}
                onChangeStep={changeStep}
                onMoveUp={moveUp}
                onMoveDown={moveDown}
                onDelete={removeStep}
                onGenerateStep={handleGenerateStepAI}
                onGenerateImage={handleGenerateSlotImageAI}
                onZoomImage={onZoomImage}
                t={t}
              />

              <button className="btn btn-primary btn-weave mt-3" onClick={addStep}>
                <i className="bi bi-plus-circle me-1"></i> {t('wz_add_step')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}



/** ***********************************
 * Identity (left column in Step 1)
 ************************************ */
const IdentityCard = React.memo(function IdentityCard({ fileInputRef, preview, onPick, onDrop, onGenerateAI, generatingAI, aiBtnRef, t }) {
  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2">
        <i className="bi bi-image"></i>
        <div className="form-label fw-bold m-0">{t('wz_identity_title')}</div>
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="w-100 rounded-4 text-center border border-2 border-secondary-subtle bg-white overflow-hidden position-relative"
        style={{ minHeight: 460, padding: preview ? 0 : '16px', cursor: 'pointer' }}
      >
        {preview ? (
          <img
            src={preview}
            alt="preview"
            className="w-100 h-100 d-block"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
          />
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ height: 430 }}>
            <i className="bi bi-upload fs-2 mb-2"></i>
            <div className="fw-semibold">{t('wz_identity_pick')}</div>
            <small className="text-muted">{t('wz_identity_hint')}</small>
          </div>
        )}
        {typeof onGenerateAI === 'function' && (
          <button
            ref={aiBtnRef}
            type="button"
            className="btn btn-primary btn-weave btn-sm rounded-pill px-3 position-absolute"
            style={{ right: 14, bottom: 14, zIndex: 5 }}
            disabled={!!generatingAI}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onGenerateAI();
            }}
          >
            <i className={`bi ${generatingAI ? 'bi-hourglass-split' : 'bi-stars'} me-2`}></i>
            {generatingAI ? t('wz_cover_generating') : (preview ? t('wz_regenerate_image') : t('wz_generate_cover'))}
          </button>
        )}

      </div>

      <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={onPick} />
    </div>
  );
});

/** ***********************************
 * TemplatePicker — selection field (Step 1)
 ************************************ */
const TemplatePicker = React.memo(function TemplatePicker({ templateKey, setTemplateKey, t }) {
  const selected = localizeTemplate(getTemplateByKey(templateKey), t);
  const visualSrc = templateVisualSrc(templateKey);

  const tr = useCallback(
    (key, fallback, vars) => {
      const out = typeof t === "function" ? t(key, vars) : key;
      return out === key ? (fallback ?? key) : out;
    },
    [t]
  );

  // Tabs: show all templates (including Relay). If current templateKey isn't in the list, append it to avoid breaking old projects.
  const tabs = useMemo(() => {
    const base = [...TEMPLATE_OPTIONS];
    const cur = sanitizeTemplateKey(templateKey);
    if (!base.some((x) => sanitizeTemplateKey(x.key) === cur)) {
      const extra = TEMPLATE_OPTIONS.find((x) => sanitizeTemplateKey(x.key) === cur);
      return extra ? [...base, extra] : base;
    }
    return base;
  }, [templateKey]);

  // Minimal definition: show "How it works" only; other sections behind a toggle.
  const [showMore, setShowMore] = useState(false);

  // Lightbox (click template visual to open full size)
  const [lightbox, setLightbox] = useState({ src: "", alt: "" });

  const closeLightbox = useCallback(() => {
    setLightbox({ src: "", alt: "" });
  }, []);

  const openLightbox = useCallback((src, alt) => {
    if (!src) return;
    setLightbox({ src, alt: alt || "" });
  }, []);

  useEffect(() => {
    if (!lightbox.src) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.src, closeLightbox]);

  const moreRows = useMemo(() => ([
    {
      id: "concept",
      icon: "bi-lightbulb",
      labelKey: "wz_template_details_concept",
      fallback: "Concept",
      value: selected.logic,
    },
    {
      id: "example",
      icon: "bi-card-text",
      labelKey: "wz_template_details_example",
      fallback: "Example",
      value: selected.engagement,
    },
    {
      id: "result",
      icon: "bi-check2-square",
      labelKey: "wz_template_details_result",
      fallback: "Result",
      value: selected.output,
    },
  ]), [selected]);

  return (
    <div className="mb-3" data-weavevo-templates="v10">
      {/* HEADER */}
      <div className="d-flex flex-column mb-3">
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-layout-text-window-reverse" aria-hidden="true"></i>
          <div className="fw-bold m-0">{tr("wz_template_label", "The layout")}</div>
        </div>
        <div className="small text-muted">{tr("wz_template_hint", "Choose how your Weavers will collaborate")}</div>
      </div>

      {/* TABS ROW */}
      <div className="wv-layout-tabs-wrap">
        <div className="wv-layout-tabs" role="tablist" aria-label={tr("wz_template_label", "The layout")}>
          {tabs.map((tpl) => {
            const active = sanitizeTemplateKey(templateKey) === sanitizeTemplateKey(tpl.key);
            const view = localizeTemplate(tpl, t);
            return (
              <button
                key={tpl.key}
                type="button"
                onClick={() => setTemplateKey(tpl.key)}
                className={`wv-layout-tab ${active ? "is-active" : ""}`}
                aria-pressed={active}
                role="tab"
                aria-selected={active}
                title={templateTooltip(tpl, t)}
              >
                <span className="wv-layout-tab-left">
                  <span className="wv-layout-tab-ico" aria-hidden="true">
                    <i className={`bi ${tpl.icon}`} />
                  </span>
                  <span className="wv-layout-tab-text">
                    <span className="wv-layout-tab-title">{view.label}</span>
                    <span className="wv-layout-tab-sub">{view.aka}</span>
                  </span>
                </span>

                {active ? (
                  <span className="wv-layout-tab-check" aria-hidden="true">
                    <i className="bi bi-check2" />
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* COMPACT DEFINITION CARD */}
      <div className="wv-layout-def-card wv-layout-def-compact">
        <div className="wv-layout-def-top">
          {/* illustration LEFT (no border) */}
          <div className="wv-layout-illus">
            {visualSrc ? (
              <button
                type="button"
                className="wv-layout-illus-btn"
                onClick={() => openLightbox(visualSrc, `${selected.label} template visual`)}
                aria-label={`Open ${selected.label} template visual`}
              >
                <img
                  src={visualSrc}
                  alt={`${selected.label} template visual`}
                  className="wv-layout-illus-img"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </button>
            ) : null}
          </div>

          {/* title RIGHT */}
          <div className="flex-grow-1">
            <div className="wv-layout-def-title">{selected.label}</div>
            <div className="wv-layout-def-sub">{selected.aka}</div>
            {selected.blurb ? <div className="wv-layout-def-blurb">{selected.blurb}</div> : null}

            {/* How it works — always visible */}
            <div className="wv-layout-how mt-3">
              <div className="wv-layout-how-title">
                <i className="bi bi-list-task me-2" aria-hidden="true"></i>
                {tr("wz_template_details_how", "How it works")}
              </div>
              <div className="wv-layout-how-text">{selected.nature}</div>
            </div>

            {/* Optional details */}
            <button
              type="button"
              className="btn btn-link p-0 mt-2 wv-layout-more"
              onClick={() => setShowMore((v) => !v)}
            >
              {showMore ? tr("wz_less_details", "Less details") : tr("wz_more_details", "More details")}
            </button>

            {showMore ? (
              <div className="wv-layout-more-grid mt-2">
                {moreRows.map((row) => (
                  <div key={row.id} className="wv-layout-more-item">
                    <div className="wv-layout-more-title">
                      <i className={`bi ${row.icon} me-2`} aria-hidden="true"></i>
                      {tr(row.labelKey, row.fallback)}
                    </div>
                    <div className="wv-layout-more-text">{row.value}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.src ? (
        <div className="wv-lightbox-backdrop" role="dialog" aria-modal="true" onClick={closeLightbox}>
          <div className="wv-lightbox" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="wv-lightbox-close" onClick={closeLightbox} aria-label="Close">
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
            <img src={lightbox.src} alt={lightbox.alt || ""} />
          </div>
        </div>
      ) : null}
    </div>
  );
});

/** ***********************************
 * ProjectFields (right column in Step 1) — includes template selection
 ************************************ */
const ProjectFields = React.memo(function ProjectFields({
  title, description,
  onTitleChange, onDescriptionChange,

  // tags
  tags, tagInput, setTagInput, addTagFromInput, removeTag,

  t,
}) {
  return (
    <div className="card border-0 rounded-4 shadow-sm mb-4">
      <div className="card-body p-4">
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label fw-bold">{t('wz_project_title_label')} <span className="">*</span></label>
            <input
              className="form-control form-control-lg rounded-4"
              value={title}
              onChange={onTitleChange}
              placeholder={t('wz_project_title_ph')}
            />
          </div>

          <div className="col-12">
            <label className="form-label fw-bold">{t('wz_project_desc_label')} <span className="">*</span></label>
            <textarea
              className="form-control rounded-4"
              rows={5}
              value={description}
              onChange={onDescriptionChange}
              placeholder={t('wz_project_desc_ph')}
            />
          </div>

          {/* Tags (required) */}
          <div className="col-12">
            <label className="form-label fw-bold mb-1">{t('wz_tags')} *</label>
            <div className="d-flex gap-2 wv-tags-row">
              <input
                className="form-control rounded-4"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder={t('wz_tags_ph')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addTagFromInput();
                  }
                }}
                onBlur={() => addTagFromInput()}
              />
              <button type="button" className="btn btn-primary btn-weave" onClick={addTagFromInput} aria-label={t('wz_add')}>
                <i className="bi bi-plus"></i>
              </button>
            </div>

            {tags?.length ? (
              <div className="mt-3 d-flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span key={tag} className="badge text-bg-light border">
                    <i className="bi bi-tag me-1"></i>{tag}
                    <button
                      type="button"
                      className="btn btn-sm btn-link ms-2 p-0"
                      onClick={() => removeTag(tag)}
                      aria-label={t('wz_delete')}
                    >
                      <i className="bi bi-x-circle"></i>
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
});


/** ***********************************
 * AccessCard (Step 1 right column)
 ************************************ */
const AccessCard = React.memo(function AccessCard({
  t,
  visibility,
  onVisibilityChange,
  users,
  loadingUsers,
  interestMap,
  selectedWeavers,
  setSelectedWeavers,
  selectedCoScens,
  toggleCoScen,
  weaversQuery,
  setWeaversQuery,
}) {
  const [open, setOpen] = useState(true);

  // NEW: search by interest (datalist) + show interests column on desktop
  const [interestQuery, setInterestQuery] = useState('');
  const interestsListIdRef = useRef('');
  if (!interestsListIdRef.current) interestsListIdRef.current = `wv-weaver-interests-${Math.random().toString(36).slice(2, 9)}`;

  const allInterests = useMemo(() => {
    const acc = [];
    try {
      (Array.isArray(ACCOUNT_PAGE_DEFAULT_INTERESTS) ? ACCOUNT_PAGE_DEFAULT_INTERESTS : [])
        .forEach((x) => { if (x) acc.push(String(x)); });
    } catch (_) { }

    // from interestMap (/user_settings)
    try {
      Object.values(interestMap || {}).forEach((arr) => {
        (Array.isArray(arr) ? arr : parseTokenList(arr)).forEach((it) => { if (it) acc.push(it); });
      });
    } catch (_) { }

    // from users (fallback)
    try {
      (Array.isArray(users) ? users : []).forEach((u) => {
        const raw = (u?.interests ?? u?.tags ?? u?.profile?.interests ?? u?.settings?.interests);
        parseTokenList(raw).forEach((it) => { if (it) acc.push(it); });
      });
    } catch (_) { }

    return Array.from(new Set(acc.map((x) => cleanToken(String(x))))).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [users, interestMap]);

  const getUserInterests = useCallback((u) => {
    if (!u) return [];
    const keyU = normalize(cleanToken(u.username));
    const keyE = normalize(cleanToken(u.email));
    const keyAttrE = normalize(cleanToken(u?.attributes?.email?.[0] || u?.attributes?.email));

    const fromMap = (interestMap && (interestMap[keyU] || interestMap[keyE] || interestMap[keyAttrE])) || [];
    const rawFallback = (u?.interests ?? u?.tags ?? u?.profile?.interests ?? u?.settings?.interests);
    const fallbackArr = parseTokenList(rawFallback);

    const arr = (Array.isArray(fromMap) && fromMap.length) ? fromMap : fallbackArr;
    return Array.isArray(arr) ? arr : parseTokenList(arr);
  }, [interestMap]);

  const findUserByKey = useCallback((key) => {
    const k = normalize(cleanToken(key));
    const arr = Array.isArray(users) ? users : [];
    if (!k) return null;
    return (
      arr.find((u) => {
        const a = normalize(cleanToken(u?.username || ""));
        const b = normalize(cleanToken(u?.email || ""));
        const c = normalize(cleanToken(u?.attributes?.email?.[0] || u?.attributes?.email || ""));
        return k === a || k === b || k === c;
      }) || null
    );
  }, [users]);

  const getAvatarSrc = useCallback((u) => {
    return (
      u?.avatar_url ||
      u?.profile_photo ||
      u?.profilePhoto ||
      u?.attributes?.avatar_url?.[0] ||
      u?.attributes?.avatarUrl?.[0] ||
      "/default-avatar.png"
    );
  }, []);

  const selectedWeaverThumbs = useMemo(() => {
    const arr = Array.isArray(selectedWeavers) ? selectedWeavers : [];
    const thumbs = [];
    arr.forEach((key) => {
      const u = findUserByKey(key);
      thumbs.push({
        key,
        profile: (u?.username || key),
        alt: (u?.fullName || u?.username || key),
        src: getAvatarSrc(u || {}),
      });
    });
    return thumbs;
  }, [selectedWeavers, findUserByKey, getAvatarSrc]);

  const toggleWeaver = useCallback((username) => {
    if (!username) return;
    setSelectedWeavers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const exists = arr.includes(username);
      return exists ? arr.filter((x) => x !== username) : [...arr, username];
    });
  }, [setSelectedWeavers]);

  const filteredUsers = useMemo(() => {
    const q = String(weaversQuery || "").trim().toLowerCase();
    const iq = String(interestQuery || "").trim().toLowerCase();
    const arr = Array.isArray(users) ? users : [];
    if (!q && !iq) return arr;

    return arr.filter((u) => {
      const name = `${u.fullName || ""} ${u.firstName || ""} ${u.lastName || ""} ${u.username || ""} ${u.email || ""}`.toLowerCase();
      if (q && !name.includes(q)) return false;

      if (iq) {
        const ints = (getUserInterests(u) || []).join(", ").toLowerCase();
        if (!ints.includes(iq)) return false;
      }

      return true;
    });
  }, [users, weaversQuery, interestQuery, getUserInterests]);

  return (
    <div className="card border-0 rounded-4 shadow-sm wv-access-card h-100">
      <div className="card-body p-3 p-md-4">
        <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-shield-lock"></i>
            <div className="fw-bold m-0">{t('wz_step2')}</div>
          </div>
        </div>

        <div className="small text-muted mb-3">{t('wz_access_in_loom')}</div>

        <div className="wv-access-options">
          <button
            type="button"
            className={`wv-access-opt ${String(visibility).toUpperCase() === 'PUBLIC' ? 'is-active' : ''}`}
            onClick={() => onVisibilityChange('PUBLIC')}
          >
            <i className="bi bi-globe2 me-2" aria-hidden="true"></i>
            {t('wz_public')}
          </button>

          <button
            type="button"
            className={`wv-access-opt ${String(visibility).toUpperCase() === 'PRIVATE' ? 'is-active' : ''}`}
            onClick={() => onVisibilityChange('PRIVATE')}
          >
            <i className="bi bi-lock me-2" aria-hidden="true"></i>
            {t('wz_private')}
          </button>

          <div className="wv-access-opt is-disabled" title="Coming soon">
            <i className="bi bi-people me-2" aria-hidden="true"></i>
            {t('wz_share_with_my_weavers')}
            <span className="badge text-bg-light border ms-2">Soon</span>
          </div>

          <button
            type="button"
            className={`wv-access-opt ${open ? 'is-open' : ''}`}
            onClick={() => setOpen((v) => !v)}
          >
            <i className="bi bi-person-lines-fill me-2" aria-hidden="true"></i>
            {t('wz_select_weavers')}
            <span className="ms-auto small text-muted">
              {Array.isArray(selectedWeavers) ? selectedWeavers.length : 0}
            </span>
            <i className={`bi ms-2 ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true"></i>
          </button>

          {open ? (
            <div className="wv-access-picker mt-2">
              <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                <div className="small fw-semibold d-flex align-items-center flex-wrap gap-2">
                  <span>
                    {t('wz_selected_weavers')}: {Array.isArray(selectedWeavers) ? selectedWeavers.length : 0}
                  </span>

                  {selectedWeaverThumbs?.length ? (
                    <span className="wv-selected-weavers-avatars" aria-label="Selected weavers">
                      {selectedWeaverThumbs.slice(0, 6).map((x) => (
                        <img
                          key={x.key}
                          src={x.src}
                          alt={x.alt}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = "/default-avatar.png";
                          }}
                        />
                      ))}

                      {selectedWeaverThumbs.length > 6 ? (
                        <span className="wv-selected-weavers-more">+{selectedWeaverThumbs.length - 6}</span>
                      ) : null}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="row g-2">
                <div className="col-12 col-lg-6">
                  <input
                    className="form-control rounded-4"
                    value={weaversQuery}
                    onChange={(e) => setWeaversQuery(e.target.value)}
                    placeholder={t('wz_search_ph')}
                  />
                </div>

                <div className="col-12 col-lg-6">
                  <input
                    className="form-control rounded-4"
                    list={interestsListIdRef.current}
                    value={interestQuery}
                    onChange={(e) => setInterestQuery(e.target.value)}
                    placeholder={t('wz_profile_interests')}
                  />
                  <datalist id={interestsListIdRef.current}>
                    {(allInterests || []).map((it) => (
                      <option key={it} value={it} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="wv-access-users mt-2">
                {loadingUsers ? (
                  <div className="text-muted small">
                    <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                    {t('wz_loading')}
                  </div>
                ) : (
                  (filteredUsers || []).slice(0, 50).map((u) => {
                    const username = u.username || u.email || "";
                    const checked = Array.isArray(selectedWeavers) ? selectedWeavers.includes(username) : false;
                    const ints = getUserInterests(u) || [];
                    return (
                      <label key={username} className="wv-access-user">
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={checked}
                          onChange={() => toggleWeaver(username)}
                        />

                        <img
                          className="wv-access-avatar"
                          src={
                            u?.avatar_url ||
                            u?.profile_photo ||
                            u?.profilePhoto ||
                            u?.attributes?.avatar_url?.[0] ||
                            u?.attributes?.avatarUrl?.[0] ||
                            "/default-avatar.png"
                          }
                          alt={u.username || username}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = "/default-avatar.png";
                          }}
                        />

                        <span className="wv-access-user-main">
                          <span className="wv-access-user-name">{u.fullName || username}</span>
                          <span className="wv-access-user-sub">{u.username || u.email || ""}</span>
                        </span>

                        <span className="wv-access-user-actions">
                          {typeof toggleCoScen === 'function' ? (
                            <button
                              type="button"
                              className={`wv-role-chip ${Array.isArray(selectedCoScens) && selectedCoScens.includes(username) ? 'is-active' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // ensure the user is also selected in weavers list
                                if (!checked) toggleWeaver(username);
                                toggleCoScen(username);
                              }}
                              title={t('wz_role_co_scenariste')}
                              aria-label={t('wz_role_co_scenariste')}
                            >
                              <i className="bi bi-feather" aria-hidden="true"></i>
                              <span className="wv-role-chip-label">{t('wz_role_co_scenariste')}</span>
                            </button>
                          ) : null}

                          <span className="wv-access-user-interests">
                            {ints.slice(0, 4).map((tag) => (
                              <span key={username + "_" + tag} className="badge text-bg-light border wv-interest-pill">
                                {tag}
                              </span>
                            ))}
                            {ints.length > 4 ? (
                              <span className="badge text-bg-light border wv-interest-pill">
                                +{ints.length - 4}
                              </span>
                            ) : null}
                          </span>
                        </span>
                      </label>
                    );
                  })
                )}
              </div>

              <div className="form-text text-muted mt-2">
                {t('wz_select_weavers')}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

/** ***********************************
 * StepsList — isolated list of StepCards
 ************************************ */
const StepsList = React.memo(function StepsList({
  steps,
  generatingIndex,
  generatingImageIds,
  onChangeStep,
  onMoveUp,
  onMoveDown,
  onDelete,
  onGenerateStep,
  onGenerateImage,
  onZoomImage,
  t,
}) {
  return (
    <div className="row g-3">
      {(steps || []).map((s, idx) => (
        <div key={s.id || idx} className="col-12 col-lg-6">
          <StepCard
            index={idx}
            step={s}
            isGenerating={generatingIndex === idx}
            isGeneratingImage={!!(generatingImageIds || {})[String(s.id || idx)]}
            onMoveUp={() => onMoveUp(s.id)}
            onMoveDown={() => onMoveDown(s.id)}
            onDelete={() => onDelete(s.id)}
            onChange={(draft) => onChangeStep(s.id, draft)}
            onGenerate={() => onGenerateStep(idx)}
            onGenerateImage={() => onGenerateImage(idx)}
            onZoomImage={onZoomImage}
            t={t}
          />
        </div>
      ))}
    </div>
  );
});

/** ***********************************
 * StepCard
 ************************************ */
const StepCard = React.memo(function StepCard({
  index, step,
  isGenerating,
  isGeneratingImage,
  onMoveUp, onMoveDown, onDelete,
  onChange,
  onGenerate,
  onGenerateImage,
  onZoomImage,
  t,
}) {
  const coords = useMemo(() => parseLatLng(step.location), [step.location]);
  const mapHref = coords
    ? `https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lng}#map=16/${coords.lat}/${coords.lng}`
    : null;


  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const illustActionsRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      const el = menuRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
    };
  }, [menuOpen]);


  useEffect(() => {
    const root = illustActionsRef.current;
    try {
      const bs = typeof window !== "undefined" ? window.bootstrap : null;
      if (!root || !bs || !bs.Tooltip) return;

      const els = Array.from(root.querySelectorAll('[data-bs-toggle="tooltip"]'));
      els.forEach((el) => {
        try {
          if (bs.Tooltip.getOrCreateInstance) {
            bs.Tooltip.getOrCreateInstance(el, { container: "body", boundary: "viewport" });
          } else {
            const existing = bs.Tooltip.getInstance(el);
            if (existing) existing.dispose();
            new bs.Tooltip(el, { container: "body", boundary: "viewport" });
          }
        } catch (_) { }
      });

      return () => {
        els.forEach((el) => {
          try {
            const inst = bs.Tooltip.getInstance(el);
            if (inst) inst.dispose();
          } catch (_) { }
        });
      };
    } catch (_) { }
  }, [t]);


  const onUsePosition = useCallback(() => {
    try {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        window.alert(t('wz_position_unavailable'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = Number(pos.coords.latitude).toFixed(6);
          const lng = Number(pos.coords.longitude).toFixed(6);
          onChange && onChange({ location: `${lat},${lng}` });
        },
        () => window.alert(t('wz_position_unavailable')),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } catch (e) {
      window.alert(t('wz_position_unavailable'));
    }
  }, [onChange, t]);


  return (
    <div className="card rounded-4 shadow-sm wv-slot-card">
      <div className="card-body p-3 p-xxl-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
          <div className="d-flex align-items-center gap-2 flex-grow-1">
            <i className="bi bi-grip-vertical text-muted fs-4 wv-slot-grip" aria-hidden="true"></i>
            <div className="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
              <div className="fw-semibold flex-shrink-0">{t('wz_step', { n: index + 1 })}</div>

              <div className="d-flex align-items-center gap-2 flex-grow-1 wv-slot-head-row">
                <div className="input-group input-group-sm wv-slot-head-where">
                  <input
                    className="form-control rounded-start-4"
                    value={step.location || ''}
                    onChange={(e) => onChange({ location: e.target.value })}
                    placeholder={t('wz_place')}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-end-4"
                    onClick={onUsePosition}
                    title={t('wz_use_position')}
                    aria-label={t('wz_use_position')}
                  >
                    📍
                  </button>
                </div>

                <input
                  className="form-control form-control-sm rounded-4 wv-slot-head-duration"
                  value={step.duration || ''}
                  onChange={(e) => onChange({ duration: e.target.value })}
                  placeholder={t('wz_duration')}
                />
              </div>
            </div>
          </div>


          {/* Actions */}
          <div className="d-flex align-items-center gap-2 flex-shrink-0">

            {/* Kebab menu */}
            <div className="position-relative flex-shrink-0 wv-slot-actions-kebab" ref={menuRef}>
              <button
                type="button"
                className="btn btn-light border rounded-3 wv-slot-kebab"
                onClick={() => setMenuOpen(v => !v)}
                aria-label="Menu"
                title="Menu"
              >
                <i className="bi bi-three-dots" aria-hidden="true"></i>
              </button>

              {menuOpen ? (
                <div className="dropdown-menu show wv-slot-menu">
                  <button type="button" className="dropdown-item" onClick={() => { setMenuOpen(false); onUsePosition(); }}>
                    📍 <span className="ms-2">{t('wz_use_position')}</span>
                  </button>

                  {mapHref ? (
                    <a
                      className="dropdown-item"
                      href={mapHref}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setMenuOpen(false)}
                    >
                      <i className="bi bi-map me-2"></i>{t('wz_open_map')}
                    </a>
                  ) : null}

                  {step.image_url ? (
                    <>
                      <div className="dropdown-divider"></div>
                      <button type="button" className="dropdown-item text-danger" onClick={() => { setMenuOpen(false); onChange({ image_url: '' }); }}>
                        <i className="bi bi-x-lg me-2"></i>{t('wz_remove_image')}
                      </button>
                    </>
                  ) : null}

                  <div className="dropdown-divider"></div>

                  <button type="button" className="dropdown-item text-danger" onClick={() => { setMenuOpen(false); onDelete && onDelete(); }}>
                    <i className="bi bi-trash me-2"></i>{t('wz_delete')}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {/* Content */}
        <div className="row g-3 align-items-stretch">
          {/* LEFT: Illustration */}
          <div className="col-12 col-lg-4">
            <div className="wv-slot-illust-box wv-slot-illust-left">
              <div className="wv-slot-illust-actions" ref={illustActionsRef} aria-label="Slot quick actions">
                <button
                  type="button"
                  className={`wv-slot-icon-action wv-slot-icon-primary ${isGeneratingImage ? 'is-busy' : ''}`}
                  onClick={(e) => { e.stopPropagation(); onGenerateImage && onGenerateImage(); }}
                  disabled={isGeneratingImage}
                  title={t('wz_replace')}
                  aria-label={t('wz_replace')}
                  data-bs-toggle="tooltip"
                  data-bs-title={t('wz_replace')}
                >
                  {isGeneratingImage ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    <i className="bi bi-image" aria-hidden="true"></i>
                  )}
                </button>

                <button
                  type="button"
                  className={`wv-slot-icon-action wv-slot-icon-secondary ${isGenerating ? 'is-busy' : ''}`}
                  onClick={(e) => { e.stopPropagation(); onGenerate && onGenerate(); }}
                  disabled={isGenerating}
                  title={t('wz_generate_desc')}
                  aria-label={t('wz_generate_desc')}
                  data-bs-toggle="tooltip"
                  data-bs-title={t('wz_generate_desc')}
                >
                  {isGenerating ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    <i className="bi bi-stars" aria-hidden="true"></i>
                  )}
                </button>
              </div>{step.image_url ? (
                <button
                  type="button"
                  className="btn p-0 border-0 bg-transparent w-100 h-100"
                  onClick={() => onZoomImage && onZoomImage(step.image_url)}
                  title={t('wz_zoom_image')}
                  aria-label={t('wz_zoom_image')}
                  style={{ cursor: "zoom-in" }}
                >
                  <img
                    className="wv-slot-illust-img"
                    src={step.image_url}
                    alt={t('wz_slot_illustration')}
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ) : (
                <div className="wv-slot-illust-placeholder">
                  <i className="bi bi-image"></i>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Fields */}
          <div className="col-12 col-lg-8 d-flex flex-column">



            <div className="mb-0 d-flex flex-column flex-grow-1">
              <label className="form-label fw-bold mb-1">{t('wz_step_desc')}</label>
              <textarea
                className="form-control rounded-4 flex-grow-1"
                rows={6}
                value={step.description || ''}
                onChange={(e) => onChange({ description: e.target.value })}
                placeholder={t('wz_step_desc_ph')}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

/** ***********************************
 * TeamTable (Step 2)
 ************************************ */
const TeamTable = React.memo(function TeamTable({ rows, showInterests, onChangeRole, t }) {
  const tx = typeof t === 'function' ? t : (k) => (WZ_I18N?.en?.[k] ?? k);
  return (
    <div className="table-responsive wv-team-table-responsive">
      <table className="table table-striped table-hover align-middle">
        <thead>
          <tr>
            <th><i className="bi bi-person"></i> {tx("wz_user")}</th>
            <th><i className="bi bi-envelope"></i> {tx("wz_email")}</th>
            {showInterests && <th><i className="bi bi-stars"></i> {tx("wz_profile_interests_col")}</th>}
            <th><i className="bi bi-tools"></i> {tx("wz_role")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(u => (
            <tr key={u.id || u.username}>
              <td>{u.firstName || ''} {u.lastName || ''} @{u.username}</td>
              <td>{u.email || u.username}</td>
              {showInterests && (
                <td className="align-middle">
                  {u.interests && u.interests.trim()
                    ? u.interests.split(',').map(s => s.trim()).filter(Boolean).map(tag => (
                      <span key={u.username + '_' + tag} className="badge bg-light text-dark border me-1 mb-1">{tag}</span>
                    ))
                    : '—'}
                </td>
              )}
              <td style={{ maxWidth: 220 }}>
                <select
                  className="form-select"
                  value={u.role || ''}
                  onChange={(e) => onChangeRole(u, e.target.value)}
                >
                  <option value="">{tx("wz_none_option")}</option>
                  <option value="contributor">{tx("wz_role_contributor")}</option>
                  <option value="co-scenariste">{tx("wz_role_co_scenariste")}</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

/** ***********************************
 * Forum/messages block (Step 2)
 ************************************ */
const ForumBlock = React.memo(function ForumBlock({ messages, newMessage, onNewMessageChange, onSendMessage, t }) {
  const tx = typeof t === 'function' ? t : (k) => (WZ_I18N?.en?.[k] ?? k);
  return (
    <div className="card border-0 rounded-4 shadow-sm">
      <div className="card-body">
        <h5><i className="bi bi-chat-dots me-2"></i>{tx("wz_forum")}</h5>
        <div className="mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {messages.map((m, i) => (
            <div key={i} className="border-bottom py-2">
              <small className="text-muted">{new Date(m.createdAt).toLocaleString()}</small>
              <div>{m.text}</div>
            </div>
          ))}
        </div>
        <div className="input-group">
          <input className="form-control" value={newMessage} onChange={onNewMessageChange} placeholder={tx("wz_message_ph")} />
          <button className="btn btn-primary" onClick={onSendMessage}>
            <i className="bi bi-send"></i>
          </button>
        </div>
      </div>
    </div>
  );
});


/** ***********************************
 * WizardStyles — inline CSS (keeps styling consistent with Attachment 1)
 ************************************ */
const WizardStyles = React.memo(function WizardStyles() {
  return (
    <style>{`
      /* === Global overflow guards (14-inch @ 100% zoom) === */
      .wizard-scope{
        width: 100%;
        max-width: 100%;
        overflow-x: clip;
      }
      .wizard-scope img,
      .wizard-scope video,
      .wizard-scope canvas{
        max-width: 100%;
      }
      /* Allow flex children to shrink (prevents horizontal overflow in Bootstrap rows) */
      .wizard-scope .row > [class*="col-"]{ min-width: 0; }
      /* Keep long user strings from pushing layout */
      .wv-access-user-main{ min-width: 0; }
      .wv-access-user-name,
      .wv-access-user-sub{
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      /* Team table: contain width inside card */
      .wv-team-table-responsive{
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }

/* === Masonry: remove blank gaps between Step 1 sections (Cover/Info/Access/Layout) === */
.wv-masonry-grid{
  display: grid;
  width: 100%;
  max-width: 100%;
  gap: 1.5rem; /* same spirit as Bootstrap g-4 */
  align-items: start;
  grid-template-columns: 1fr; /* mobile default */
}
.wv-masonry-item{ min-width: 0; }
@media (min-width: 992px){
  .wv-masonry-grid{
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-auto-rows: 10px; /* base row height for span calc */
    grid-auto-flow: row;  /* keep order (no dense re-ordering) */
  }
  .wv-masonry-item{
    grid-row-end: span var(--wv-row-span, 1);
  }
}
/* Grid already manages spacing; remove extra bottom margins from top-level blocks */
.wv-masonry-item [data-wv-masonry-measure] > .mb-3,
.wv-masonry-item [data-wv-masonry-measure] > .mb-4,
.wv-masonry-item [data-wv-masonry-measure] > .card.mb-3,
.wv-masonry-item [data-wv-masonry-measure] > .card.mb-4{
  margin-bottom: 0 !important;
}


      /* Fix flex + form-control overflow (Tags input row) */
      .wv-tags-row{
        width: 100%;
      }
      .wv-tags-row .form-control{
        flex: 1 1 auto;
        min-width: 0;
        width: auto !important;
      }
      .wv-tags-row .btn{
        flex: 0 0 auto;
        white-space: nowrap;
      }


      /* === Layout block (matches screenshot) === */
.wv-layout-tabs-wrap{
  border: 1px solid rgba(13, 110, 253, .22);
  background: #fff;
  border-radius: 20px;
  padding: 10px;
  box-shadow: 0 14px 40px rgba(15, 23, 42, .06);
  max-width: 100%;
  overflow: hidden; /* prevents any child from exceeding page width */
}

.wv-layout-tabs{
  width: 100%;
  display:flex;
  gap: 10px;
  align-items:stretch;
  overflow-x:auto;               /* mobile/tablet: horizontal scroll */
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.wv-layout-tabs::-webkit-scrollbar{ display:none; }

/* Laptop/Desktop (incl. 14"): 3 columns / 2 rows grid (no horizontal scroll) */
@media (min-width: 992px){
  .wv-layout-tabs{
    display:grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    overflow: visible;
    scrollbar-width: auto;
  }
}

/* Ensure the tab button never pushes beyond the container */
.wv-layout-tab{
  border: 1px solid rgba(13, 110, 253, .22);
  background: #fff;
  border-radius: 16px;
  padding: 9px 11px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 12px;
  cursor:pointer;
  text-align:left;
  transition: transform .08s ease, box-shadow .12s ease, border-color .12s ease, background .12s ease;
  max-width: 100%;
  min-width: 0;
}

/* Mobile/tablet scroll cards: give a comfy min width */
@media (max-width: 991.98px){
  .wv-layout-tab{
    flex: 0 0 auto;
    min-width: 240px;
  }
}

.wv-layout-tab:hover{
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(15, 23, 42, .08);
}
.wv-layout-tab:focus-visible{
  outline: none;
  box-shadow: 0 0 0 4px rgba(13,110,253,.22);
}
.wv-layout-tab.is-active{
  border-color: var(--bs-primary, #0d6efd);
  background: var(--bs-primary, #0d6efd);
  box-shadow: 0 0 0 3px rgba(13,110,253,.18);
}

.wv-layout-tab-left{
  display:flex;
  align-items:center;
  gap: 12px;
  min-width: 0;
}
.wv-layout-tab-ico{
  width: 38px;
  height: 38px;
  border-radius: 14px;
  background: rgba(13,110,253,.08);
  border: 1px solid rgba(13,110,253,.18);
  display:flex;
  align-items:center;
  justify-content:center;
  flex: 0 0 auto;
}
.wv-layout-tab-ico > i{
  font-size: 1.05rem;
  color: rgba(13,110,253,.95);
}

.wv-layout-tab-text{
  display:flex;
  flex-direction:column;
  min-width: 0;
}
.wv-layout-tab-title{
  font-weight: 600;
  font-size: .85rem;   /* smaller like your screenshot */
  line-height: 1.1;
  color: rgba(13,110,253,.95);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wv-layout-tab-sub{
  margin-top: 2px;
  font-size: .80rem;   /* smaller like your screenshot */
  color: rgba(13,110,253,.80);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wv-layout-tab.is-active .wv-layout-tab-title,
.wv-layout-tab.is-active .wv-layout-tab-sub{
  color: rgba(255,255,255,.96);
}

.wv-layout-tab.is-active .wv-layout-tab-ico{
  background: rgba(255,255,255,.22);
  border-color: rgba(255,255,255,.32);
}
.wv-layout-tab.is-active .wv-layout-tab-ico > i{
  color: rgba(255,255,255,.96);
}

.wv-layout-tab-check{
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: rgba(255,255,255,.22);
  border: 1px solid rgba(255,255,255,.35);
  color: rgba(255,255,255,.98);
  display:flex;
  align-items:center;
  justify-content:center;
  flex: 0 0 auto;
}
.wv-layout-tab-check > i{ font-size: .90rem; }

/* Definition card (below tabs) */
.wv-layout-def-card{
  border: 1px solid rgba(13, 110, 253, .22);
  border-radius: 26px;
  background: #fff;
  padding: 18px;
  margin-top: 14px;
}

.wv-layout-def-top{
  display:flex;
  gap: 18px;
  align-items:center;
}
@media (max-width: 576px){
  .wv-layout-def-top{ flex-direction:column; align-items:flex-start; }
}

.wv-layout-illus{
  width: 190px;
  flex: 0 0 190px;
  display:flex;
  align-items:center;
  justify-content:center;
}
@media (max-width: 576px){
  .wv-layout-illus{ width: 100%; flex: 0 0 auto; justify-content:flex-start; }
}

.wv-layout-illus-btn{
  border: 0 !important;
  padding: 0 !important;
  background: transparent !important;
  border-radius: 16px;
  cursor: zoom-in;
}
.wv-layout-illus-img{
  width: 160px;
  max-width: 160px;
  height: auto;
  object-fit: contain;
  border: 0 !important;         /* no border around illustration */
  background: transparent !important;
  display:block;
}
@media (max-width: 576px){
  .wv-layout-illus-img{ width: 100%; max-width: 260px; }
}

.wv-layout-def-title{
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  color: rgba(15, 23, 42, .96);
}
.wv-layout-def-sub{
  margin-top: 2px;
  color: rgba(51, 65, 85, .85);
  font-weight: 700;
  font-size: 0.85rem;
}
.wv-layout-def-blurb{
  margin-top: 10px;
  color: rgba(51, 65, 85, .92);
  line-height: 1.35;
}

.wv-layout-def-divider{
  border-top: 1px solid rgba(15, 23, 42, .08);
  margin: 14px 0 0;
}

/* Accordion rows */
.wv-acc{
  margin-top: 6px;
}
.wv-acc-item{
  border-top: 1px solid rgba(15, 23, 42, .08);
}
.wv-acc-btn{
  width:100%;
  border: 0;
  background: transparent;
  padding: 12px 2px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 12px;
  text-align:left;
}
.wv-acc-btn:hover{
  background: rgba(15, 23, 42, .02);
}
.wv-acc-left{
  display:flex;
  align-items:center;
  gap: 12px;
  min-width: 0;
}
.wv-acc-ico{
  width: 30px;
  height: 30px;
  border-radius: 12px;
  background: rgba(15, 23, 42, .04);
  border: 1px solid rgba(13,110,253,.18);
  display:flex;
  align-items:center;
  justify-content:center;
  flex: 0 0 auto;
}
.wv-acc-ico > i{
  color: rgba(15, 23, 42, .65);
}
.wv-acc-title{
  font-weight: 800;
  color: rgba(15, 23, 42, .92);
}
.wv-acc-chevron{
  color: rgba(51, 65, 85, .70);
  transition: transform .12s ease;
  flex: 0 0 auto;
}
.wv-acc-chevron.is-open{
  transform: rotate(180deg);
}

.wv-acc-panel{
  padding: 0 6px 12px 44px;
}
.wv-acc-panel-inner{
  color: rgba(51, 65, 85, .95);
  line-height: 1.45;
}

@media (max-width: 992px){
  .wv-layout-tab{ min-width: 160px; }
}

      .btn-weave{
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: .5rem;
        box-shadow: 0 6px 18px rgba(0,0,0,.06);
      }
      .btn-weave:disabled{
        box-shadow: none;
      }


/* === Step 3: AI generator (Draft the Layout) === */
.wv-ai-block{
  border: 1px solid rgba(13, 110, 253, .22);
  background: #fff;
  padding: 16px;
}
@media (min-width: 768px){
  .wv-ai-block{ padding: 18px; }
}
.wv-ai-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap: 14px;
}
.wv-ai-textarea{
  min-height: 150px;
  padding: 14px 14px;
}
.wv-ai-bottom{
  display:flex;
  align-items:flex-end;
  justify-content:space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 14px;
}
.wv-ai-slot{
  flex: 1 1 360px;
  max-width: 520px;
}
.wv-stepper{
  display:flex;
  align-items:center;
  border: 1px solid rgba(15, 23, 42, .12);
  border-radius: 999px;
  overflow:hidden;
  background:#fff;
  height: 38px;
  max-width: 150px;
}
.wv-stepper-btn{
  width: 44px;
  height: 38px;
  border: 0;
  background: transparent;
  display:flex;
  align-items:center;
  justify-content:center;
  color: rgba(15, 23, 42, .75);
}
.wv-stepper-btn:hover{
  background: rgba(15, 23, 42, .04);
}
.wv-stepper-input{
  width: 90px;
  height: 38px;
  border: 0;
  outline: none;
  text-align:center;
  font-weight: 800;
  color: rgba(15, 23, 42, .9);
}
.wv-stepper-input::-webkit-outer-spin-button,
.wv-stepper-input::-webkit-inner-spin-button{
  -webkit-appearance: none;
  margin: 0;
}
.wv-stepper-input[type=number]{
  -moz-appearance: textfield;
}
.wv-slot-chips{
  display:flex;
  flex-wrap:wrap;
  gap: .5rem;
  margin-top: .6rem;
}
.wv-chip-btn{
  border: 1px solid rgba(13, 110, 253, .22);
  background: #fff;
  border-radius: 999px;
  padding: .22rem .62rem;
  font-size: .85rem;
  font-weight: 700;
  color: rgba(15, 23, 42, .85);
}
.wv-chip-btn:hover{
  background: rgba(59, 130, 246, .06);
  border-color: rgba(59, 130, 246, .35);
}
.wv-chip-btn.is-active{
  background: rgba(59, 130, 246, .10);
  border-color: rgba(59, 130, 246, .55);
}
.wv-ai-action{
  margin-left: auto;
}
.wv-ai-generate-btn{
  border-radius: 999px;
  padding-left: 1rem;
  padding-right: 1rem;
}
@media (max-width: 576px){
  .wv-ai-action{ width: 100%; }
  .wv-ai-generate-btn{ width: 100%; justify-content:center; }
}

.btn-group-sm>.btn, .btn-sm {
        --bs-btn-padding-y: 0.25rem;
        --bs-btn-padding-x: 0.5rem;
        --bs-btn-font-size: 0.875rem;
        --bs-btn-border-radius: var(--bs-border-radius-sm);
      }


      .wiz-step-inactive{
        opacity: .78;
      }
      .wiz-step-inactive:hover,
      .wiz-step-inactive:focus{
        opacity: 1;
      }

      
      /* Slot illustration (responsive) */
      .wv-section-anchor{
        scroll-margin-top: 96px;
      }
      .wv-section-card{
        border: 1px solid rgba(0,0,0,.08);
      }
      .wv-slot-card{
        border: 1px solid rgba(0,0,0,.10);
      }

      .wv-slot-grip{
        cursor: grab;
      }
      .wv-slot-kebab{
        width: 44px;
        height: 38px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .wv-slot-actions-kebab{
        display:block;
      }
      .wv-slot-subtitle{
        max-width: 520px;
        line-height: 1.15;
      }
      .wv-slot-head-row{
  flex-wrap: nowrap;
  min-width: 0;
}
.wv-slot-head-where{
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}
.wv-slot-head-duration{
  flex: 0 0 120px;
  width: 120px;
  min-width: 96px;
}

@media (max-width: 575.98px){
  .wv-slot-head-row{
    flex-wrap: wrap;
  }
  .wv-slot-head-duration{
    width: 100%;
    min-width: 0;
  }
  .wv-slot-head-where{
    flex: 1 1 100%;
    min-width: 0;
  }
}
      .wv-slot-btnrow{
        display:grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: .5rem;
        align-items: stretch;
      }
      @media (max-width: 991.98px){
        .wv-slot-btnrow{
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .wv-slot-btnrow .btn:nth-child(3){
          grid-column: 1 / -1;
        }
      }
      @media (max-width: 575.98px){
        .wv-slot-btnrow{
          grid-template-columns: 1fr;
        }
        .wv-slot-btnrow .btn:nth-child(3){
          grid-column: auto;
        }
      }

      .wv-slot-menu{
        right: 0 !important;
        left: auto !important;
        top: 50px;
        min-width: 220px;
        max-width: min(260px, calc(100vw - 24px));
        border-radius: 14px;
        box-shadow: 0 10px 28px rgba(0,0,0,.12);
        z-index: 1060;
      }
      .wv-slot-illust-left{
        height: 100%;
        min-height: 220px;
        aspect-ratio: 4 / 3;
      }
      .wv-slot-duration{
        width: 140px;
        min-width: 120px;
      }
      @media (max-width: 992px){
        .wv-slot-duration{
          width: 100%;
          min-width: 0;
          flex: 1 1 160px;
        }
      }

      /* 14-inch screens: keep 2 slots per row (xl) without cutting content */
      @media (min-width: 1200px) and (max-width: 1399.98px){
        /* Step 2 (Team sharing) — stack at xl, keep cards compact */
        .wizard-scope .card-body.p-4{ padding: 1rem !important; }
        .wizard-scope .container-fluid{ padding-left: .75rem; padding-right: .75rem; }
    
        /* Slightly tighten the card to fit two columns comfortably */
        .wv-slot-card .card-body{
          padding: 0.9rem !important;
        }

        /* Remove extra gap between the slot header and its content (14-inch) */
        .wv-slot-card .card-body > .d-flex.align-items-center.justify-content-between{
          margin-bottom: .6rem !important;
        }

        /* Reduce inner gutters a bit to avoid wasted space (14-inch) */
        .wv-slot-card .row.g-3{
          --bs-gutter-y: .65rem;
          --bs-gutter-x: .75rem;
        }

        /* Keep illustration + fields aligned so the button row sits on the same baseline as the image */
        .wv-slot-card .row.align-items-stretch{
          align-items: stretch !important;
        }

        /* Let the illustration fill the slot height on xl (no empty gap under the image) */
        .wv-slot-illust-left{
          height: 100% !important;
          min-height: 200px;
        }

        /* Keep the duration input compact */
        .wv-slot-duration{
          width: 120px;
          min-width: 110px;
        }

        /* Reduce textarea minimum height a bit on xl */
        .wv-slot-card textarea.form-control{
          min-height: 84px;
        }
      
        /* Smaller buttons on 14-inch screens */
        .wizard-scope .btn:not(.btn-link),
        .wizard-scope .btn-group-sm>.btn,
        .wizard-scope .btn-sm{
          --bs-btn-font-size: 0.875rem;
        }
      }



      .wv-slot-illust-box{
  width:100%;
  border-radius: 18px;
  overflow:hidden;
  border: 1px solid rgba(0,0,0,.08);
  background: #f6f7f9;
  position: relative;
}
.wv-slot-illust-actions{
  position:absolute;
  top: 10px;
  right: 10px;
  z-index: 8;
  display:flex;
  flex-direction: column;
  gap: .5rem;
  pointer-events: none;
}
.wv-slot-icon-action{
  pointer-events: auto;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 1px solid rgba(13, 110, 253, .18);
  background: rgba(255,255,255,.90);
  color: rgba(13, 110, 253, .95);
  display:flex;
  align-items:center;
  justify-content:center;
  box-shadow: 0 10px 24px rgba(0,0,0,.10);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: transform .12s ease, box-shadow .12s ease, background .12s ease;
  user-select: none;
}
.wv-slot-icon-action:hover{
  transform: translateY(-1px);
  box-shadow: 0 14px 30px rgba(0,0,0,.14);
  background: rgba(255,255,255,.98);
}
.wv-slot-icon-action:disabled{
  opacity: .65;
  cursor: not-allowed;
  transform: none;
}
.wv-slot-icon-secondary{
  border-color: rgba(13, 110, 253, .14);
  color: rgba(15, 23, 42, .85);
}
.wv-slot-icon-action i{
  font-size: 1.05rem;
}
@media (max-width: 575.98px){
  .wv-slot-illust-actions{
    top: 8px;
    right: 8px;
    gap: .4rem;
  }
  .wv-slot-icon-action{
    width: 38px;
    height: 38px;
  }
}
.wv-slot-illust-img{
        width:100%;
        height:100%;
        object-fit: cover;
        display:block;
      }
      .wv-slot-illust-placeholder{
        width:100%;
        height:100%;
        display:flex;
        align-items:center;
        justify-content:center;
        font-size: 2.2rem;
        color: rgba(0,0,0,.35);
      }
      @media (max-width: 768px){
        .wv-slot-illust-img{ max-height: 40vh; }
        .wv-slot-illust-placeholder{ height: 40vh; min-height: 180px; }
      }

/* Compact team chips */
      .selected-chips{
        display:flex;
        flex-wrap:wrap;
        gap:.6rem;
      }
      .chip{
        display:inline-flex;
        align-items:center;
        gap:.55rem;
        padding:.55rem .75rem;
        border-radius:999px;
        border:1px solid rgba(0,0,0,.08);
        background:#fff;
        box-shadow:0 2px 10px rgba(0,0,0,.04);
        transition:transform .12s ease, box-shadow .12s ease;
        cursor:pointer;
        max-width:100%;
      }
      .chip:hover{
        transform:translateY(-1px);
        box-shadow:0 6px 18px rgba(0,0,0,.08);
      }
      .chip__avatar{
        width:34px;
        height:34px;
        border-radius:999px;
        object-fit:cover;
        border:1px solid rgba(0,0,0,.10);
        flex:0 0 auto;
      }
      .chip__name{
        font-weight:600;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
        max-width:180px;
      }
      .chip__meta{
        font-size:.8rem;
        opacity:.7;
        white-space:nowrap;
      }
      .chip__icon{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        width:26px;
        height:26px;
        border-radius:999px;
        flex:0 0 auto;
      }
      .chip--success{
        border-color: rgba(25,135,84,.25);
      }
      .chip--success .chip__icon{
        background: rgba(25,135,84,.10);
        color: #198754;
      }
      .chip--primary{
        border-color: rgba(0,0,0,.25);
      }
      .chip--primary .chip__icon{
        background: rgba(0,0,0,.10);
        color: #111827;
      }

      /* Template picker (selection field) */
      .wv-template-pill{
        width:100%;
        border:1px solid rgba(0,0,0,.08);
        background:#fff;
        border-radius:14px;
        padding:.55rem .6rem;
        display:flex;
        align-items:center;
        gap:.55rem;
        text-align:left;
        cursor:pointer;
        transition:transform .12s ease, border-color .12s ease, background .12s ease;
        line-height:1.1;
        min-height:72px;
      }
      .wv-template-pill:hover{
        border-color: rgba(13,110,253,.55);
        background: rgba(13,110,253,.03);
        transform: translateY(-1px);
      }
      .wv-template-pill.is-active{
        border-color: rgba(13,110,253,.85);
        background: rgba(13,110,253,.08);
      }
            .wv-template-thumb{
        width: 44px;
        height: 38px;
        border-radius: 12px;
        object-fit: cover;
        border: 1px solid rgba(0,0,0,.08);
        flex: 0 0 auto;
        background: rgba(0,0,0,.02);
      }
.wv-template-pill i{
        font-size:16px;
        opacity:.85;
        flex:0 0 auto;
      }
      .wv-template-pill.is-active i{ opacity:1; }
      .wv-template-text{ display:flex; flex-direction:column; min-width:0; }
      .wv-template-label{
        font-size:.84rem;
        font-weight:500;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
      }
      .wv-template-aka{
        font-size:.72rem;
        opacity:.65;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
      }
      .wv-template-details{
        border:1px solid rgba(0,0,0,.06);
        border-radius:14px;
        padding: .45rem .6rem;
        background: rgba(0,0,0,.015);
      }
      .wv-template-details summary{
        cursor:pointer;
        list-style:none;
        outline:none;
      }
      .wv-template-details summary::-webkit-details-marker{ display:none; }
      .wv-template-details-k{ font-weight:500; }
            .wv-layout-row{
        --wv-layout-max: 300px;
        --wv-tpl-visual-h: 240px;
        --wv-tpl-text-h: 190px;
      }
      @media (min-width: 992px){
        .wv-layout-row{ height: var(--wv-layout-max); min-height: var(--wv-layout-max); }
      }

      .wv-template-grid{
        min-height: var(--wv-layout-max);
      }
      .wv-template-grid-tight{
        height: 100%;
      }

      /* Unified layout picker: single block (list + definition) */
      .wv-template-unified-card{
        height: 100%;
        overflow: hidden;
      }
      @media (max-width: 991.98px){
        .wv-template-unified-card{ height: auto; }
      }

      .wv-template-unified-inner{
        display: grid;
        grid-template-columns: minmax(260px, 40%) minmax(0, 60%);
        gap: 14px;
        height: 100%;
        min-height: 0;
        align-items: stretch;
      }
      @media (max-width: 991.98px){
        .wv-template-unified-inner{
          grid-template-columns: 1fr;
          height: auto;
          gap: 12px;
        }
      }

      .wv-template-unified-left,
      .wv-template-unified-right{
        min-width: 0;
        min-height: 0;
      }

      .wv-template-unified-left{
        height: 100%;
      }
      .wv-template-unified-right{
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      @media (min-width: 992px){
        .wv-template-unified-left{
          padding-right: 10px;
          border-right: 1px solid rgba(0,0,0,.08);
        }
        .wv-template-unified-right{
          padding-left: 10px;
        }
      }

      .wv-template-def-card{
        height: 100%;
        overflow: hidden;
      }
      .wv-template-def-card .card-body{
        height: 100%;
        min-height: 0;
      }
      @media (min-width: 992px){
        .wv-template-def-card{ min-height: var(--wv-layout-max); }
      }
      @media (max-width: 991.98px){
        .wv-template-def-card{ height: auto; }
      }
      .wv-template-def-inner{
        display:flex;
        gap: 16px;
        align-items: center;
        height: 100%;
      }
      .wv-template-def-visual{
        flex: 0 0 30%;
        max-width: 30%;
        min-width: 0;
        display:flex;
        align-items:center;
        justify-content:center;
      }
      .wv-template-def-text{
        flex: 1 1 auto;
        min-width: 0;
        display:flex;
        align-items:center;
        justify-content:center;
      }

      .wv-template-visual-wrap{
        background: transparent;
        border: 0;
        border-radius: 0;
        overflow: visible;
        display:flex;
        align-items:center;
        justify-content:center;
        width: 100%;
        padding: 0;
      }
      .wv-template-visual-wrap-left{ justify-content:flex-start; }
      .wv-template-visual-wrap-right{ justify-content:center; }
      .wv-zoom-btn-left{ margin-right: auto; }
      .wv-zoom-btn-right{ margin-left: auto; }

      .wv-template-visual{
        max-width: 100%;
        width: auto;
        height: auto;
        max-height: var(--wv-tpl-visual-h, 180px);
        object-fit: contain;
        display: block;
      }
      .wv-template-visual-big{ max-height: var(--wv-tpl-visual-h, 240px); }
      .wv-template-visual-xl{ max-height: var(--wv-tpl-visual-h-xl, 280px); }

      .wv-zoom-btn-fill{
        width: auto;
        height: auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
      }
      .wv-template-details-text{
        max-height: none;
        overflow: visible;
        display: block;
        text-align: left;
        white-space: normal;
      }

/* Click-to-zoom for template visual */
      .wv-zoom-btn{
        width: 100%;
        border: 0;
        padding: 0;
        background: transparent;
        display: block;
        cursor: zoom-in;
      }
      .wv-zoom-btn:focus-visible{
        outline: 3px solid rgba(0,0,0,.35);
        outline-offset: 3px;
        border-radius: 14px;
      }

      /* Lightbox */
      .wv-lightbox-backdrop{
        position: fixed;
        inset: 0;
        z-index: 2000;
        background: rgba(0,0,0,.72);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 18px;
      }
      .wv-lightbox{
        position: relative;
        width: min(1200px, 96vw);
        height: min(92vh, 860px);
        border-radius: 18px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 18px 60px rgba(0,0,0,.55);
      }
      .wv-lightbox img{
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
        background: #fff;
      }
      .wv-lightbox-close{
        position: absolute;
        top: 10px;
        right: 10px;
        width: 40px;
        height: 40px;
        border-radius: 999px;
        border: 1px solid rgba(0,0,0,.10);
        background: rgba(255,255,255,.85);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }
      .wv-lightbox-close:hover{
        background: rgba(255,255,255,.95);
      }

/* tight grid for template buttons: 2 columns, controlled small gap */
.wv-template-grid-tight{
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  align-content: start;
}

/* mobile: 1 column */
@media (max-width: 767.98px){
  .wv-template-grid-tight{
    grid-template-columns: 1fr;
    height: auto;
    min-height: unset;
  }
}

@media (min-width: 992px){
  .wv-template-grid-tight{
    height: 100%;
    min-height: var(--wv-layout-max);
  }
}

/* make sure pills don't create extra spacing */
.wv-template-pill{
  width: 100%;
  margin: 0 !important;
}


/* Tight 2-col layout: text LEFT and close to the visual, no overflow */
.wv-template-def-inner-tight{
  display: grid;
  grid-template-columns: minmax(200px, 30%) minmax(0, 70%);
  gap: 8px;
  align-items: center;
  align-content: start;
  height: 100%;
  min-height: 0;
}

/* Mobile: stack */
@media (max-width: 991.98px){
  .wv-template-def-inner-tight{
    grid-template-columns: 1fr;
    gap: 10px;
    height: auto;
  }
}

/* push the visual to the edge so text is closer */
.wv-template-def-inner-tight .wv-template-visual-wrap-right{
  justify-content: flex-end;
}

/* Text block: left aligned + starts near the visual */
.wv-template-def-text{
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
}

.wv-template-details-text{
  width: 100%;
  text-align: left;
}

/* Each row: label + value, keep it compact and prevent overflow */
.wv-template-detail-row{
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: 8px;
  align-items: baseline;
  padding: 2px 0;
}

/* Keep label from breaking */
.wv-template-details-k{
  white-space: nowrap;
  opacity: .85;
}

/* Value: clamp to avoid exceeding the card height */
.wv-template-details-v{
  min-width: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  white-space: normal;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}


      .scale-up { transition: all 0.25s ease-in-out; }
      .scale-up:hover { transform: scale(1.03); }

      .fade-in { animation: fadeIn 0.25s ease-in-out; }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }


/* Access card */
.wv-access-card{ min-height: 0; height: auto; }
.wv-access-options{ display:flex; flex-direction:column; gap: .55rem; }
.wv-access-opt{
  width:100%;
  display:flex;
  align-items:center;
  gap:.25rem;
  border:1px solid rgba(15,23,42,.12);
  background:#fff;
  border-radius: 14px;
  padding:.62rem .75rem;
  font-weight: 700;
  color: rgba(15,23,42,.86);
}
.wv-access-opt:hover{ background: rgba(59,130,246,.05); border-color: rgba(59,130,246,.25); }
.wv-access-opt.is-active{ border-color: rgba(59,130,246,.55); box-shadow: 0 0 0 3px rgba(59,130,246,.12); }
.wv-access-opt.is-disabled{
  opacity:.65;
  cursor:not-allowed;
  user-select:none;
}
.wv-access-opt.is-open{ border-color: rgba(15,23,42,.25); }
.wv-access-picker{
  border:1px solid rgba(15,23,42,.10);
  border-radius: 16px;
  padding: .75rem;
  background: rgba(248,250,252,.8);
}
.wv-access-users{
  max-height: 220px;
  overflow:auto;
  padding-right: .25rem;
  display:flex;
  flex-direction:column;
  gap: .35rem;
}
.wv-access-user{
  display:flex;
  align-items:flex-start;
  gap:.5rem;
  padding:.45rem .5rem;
  border-radius: 12px;
  border:1px solid rgba(15,23,42,.08);
  background:#fff;
  cursor:pointer;
}
.wv-access-user:hover{ background: rgba(59,130,246,.04); border-color: rgba(59,130,246,.20); }

/* Selected Weavers — title thumbs */
.wv-selected-weavers-avatars{
  display:inline-flex;
  align-items:center;
  gap:.25rem;
}
.wv-selected-weavers-avatars img{
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(15,23,42,.18);
  background: #e2e8f0;
  display:block;
}
.wv-selected-weavers-more{
  height: 22px;
  padding: 0 .45rem;
  display:inline-flex;
  align-items:center;
  border-radius: 999px;
  border: 1px solid rgba(15,23,42,.18);
  background:#fff;
  color: rgba(15,23,42,.72);
  font-size: .75rem;
  font-weight: 700;
}

/* Weaver row actions */
.wv-access-user-actions{
  margin-left: auto;
  display:flex;
  align-items:flex-start;
  gap: .45rem;
}

/* Interests column: desktop only */
.wv-access-user-interests{
  display:none;
  flex-wrap: wrap;
  justify-content:flex-end;
  gap: .25rem;
  max-width: 360px;
}
@media (min-width: 992px){
  .wv-access-user-interests{ display:flex; }
}
.wv-interest-pill{
  font-size: .72rem;
  padding: .22rem .45rem;
  border-radius: 999px;
}

/* Co-scénariste chip */
.wv-role-chip{
  border: 1px solid rgba(15,23,42,.16);
  background:#fff;
  border-radius: 999px;
  padding: .18rem .5rem;
  font-size: .75rem;
  font-weight: 700;
  color: rgba(15,23,42,.72);
  display:inline-flex;
  align-items:center;
  gap: .35rem;
  line-height: 1;
  white-space: nowrap;
}
.wv-role-chip:hover{ background: rgba(59,130,246,.05); border-color: rgba(59,130,246,.25); }
.wv-role-chip.is-active{
  background: rgba(255,255,255,.22);
  border-color: rgba(59,130,246,.35);
  color: rgba(37,99,235,1);
}
.wv-role-chip-label{ display:none; }
@media (min-width: 576px){
  .wv-role-chip-label{ display:inline; }
}


.wv-access-avatar{
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 40px;
  display:block;
  border: 1px solid rgba(15,23,42,.12);
  background: #e2e8f0;
}
@media (max-width: 420px){
  .wv-access-avatar{
    width: 34px;
    height: 34px;
    flex-basis: 34px;
  }
}

.wv-access-user-main{ display:flex; flex-direction:column; line-height:1.2; flex: 1 1 auto; min-width: 0; }
.wv-access-user-name{ font-weight: 700; font-size: .92rem; }
.wv-access-user-sub{ font-size: .78rem; color: rgba(15,23,42,.60); }

/* Template compact */
.wv-layout-def-compact .wv-layout-def-blurb{ margin-top:.25rem; }
.wv-layout-how-title{ font-weight: 600; margin-bottom: .25rem; }
.wv-layout-how-text{ color: rgba(15,23,42,.78);font-size: .85rem }
.wv-layout-more{ font-weight: 700; text-decoration: none; }
.wv-layout-more:hover{ text-decoration: underline; }
.wv-layout-more-grid{
  display:grid;
  gap:.75rem;
  margin-top:.25rem;
}
.wv-layout-more-item{
  border:1px solid rgba(15,23,42,.08);
  border-radius: 14px;
  padding: .65rem .75rem;
  background:#fff;
}
.wv-layout-more-title{ font-weight: 600; font-size: 0.85rem; margin-bottom: .2rem; }
.wv-layout-more-text{ color: rgba(15,23,42,.76); }

/* Final actions spacing */
.wv-final-actions{ padding-bottom: 12px; }
    `}
    </style>
  );
});